import React, { useState, useEffect, useMemo, useRef, useContext, createContext } from 'react';
import {
  ComposedChart, LineChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { PlanDeTravailProvider, BoutonEpingler } from './PlanDeTravail';
import { useEcran } from './useEcran';

/* =============================================================================
   BusinessTab v5 — bascule "vrais clients" / "tous les visiteurs"
   ─────────────────────────────────────────────────────────────────────────
   Toute la page (KPI, cartes, cercles, heatmap) recalcule sur la base
   choisie par l'utilisateur. Par defaut : vrais clients (staff exclu).
   ============================================================================= */

const COL = {
  bg: '#f5f6f8', surface: '#ffffff', border: '#e4e7eb', borderStrong: '#cdd3da',
  ink900: '#111827', ink700: '#3d4451', ink500: '#6b7280', ink400: '#9aa2af',
  bgPale: '#eaf1f3',
  amber: '#92620d', amberBg: '#fdf6e9', amberBorder: '#eed9ad',
  red: '#a42a1f', redBg: '#fdf0ee', redBorder: '#f0c4bd',
  green: '#1c7a42', greenBg: '#eaf6ee',
  accent: '#0f2e3d',
};
const SHADOW = '0 1px 2px rgba(16,24,40,.04), 0 1px 1px rgba(16,24,40,.03)';
const SHADOW_HOVER = '0 4px 12px rgba(16,24,40,.08), 0 2px 4px rgba(16,24,40,.04)';

const CAT = ['#0f2e3d', '#1c4a5c', '#2f6577', '#457f8f', '#5c99a6'];
const SHOP_TINT = {
  'GOLF SOKRA': CAT[0], 'MANAR CITY': CAT[1], 'TUNISIA MALL': CAT[2],
  'AVENUE DE PARIS': CAT[3], 'CENTRAL PARK': CAT[4],
};
const GROUPE_TINT = { solo: CAT[0], grp2: CAT[1], grp3: CAT[2], grp4p: CAT[3] };
const GROUPE_LABEL = { solo: 'Seul', grp2: 'Duo', grp3: 'Trio', grp4p: '4 et plus' };

/* --- Palette sombre, utilisee uniquement dans le plan de travail ----------
   Les graphes sont ecrits avec les constantes claires ci-dessus. Plutot que de
   reecrire chaque couleur, chaque composant concerne lit sa palette via
   `useTheme()` et masque les constantes du module par une variable de meme
   nom : le corps du composant reste inchange, seule la source des couleurs
   change. C'est ce qui permet au meme graphe de s'afficher sur fond clair dans
   le dashboard et sur fond nuit dans le plan de travail. */
const COL_SOMBRE = {
  bg: '#0d1b24', surface: '#132630', border: 'rgba(124,190,210,.20)', borderStrong: 'rgba(124,190,210,.36)',
  ink900: '#e8f4f8', ink700: '#c3d9e1', ink500: '#8fa9b4', ink400: '#6d8794',
  bgPale: 'rgba(90,215,235,.14)',
  amber: '#e0a63c', amberBg: 'rgba(224,166,60,.14)', amberBorder: 'rgba(224,166,60,.32)',
  red: '#ff8a76', redBg: 'rgba(255,138,118,.14)', redBorder: 'rgba(255,138,118,.32)',
  green: '#4ad98a', greenBg: 'rgba(74,217,138,.14)',
  accent: '#5ad7eb',
};
const SHADOW_SOMBRE = '0 1px 2px rgba(0,0,0,.45)';
const SHADOW_SOMBRE_HOVER = '0 6px 18px rgba(0,0,0,.55)';

/* Les teintes claires (bleu petrole tres fonce) disparaitraient sur fond nuit :
   la serie categorielle est reprise en clair, avec des teintes franchement
   distinctes pour rester lisible a cinq series. */
const CAT_SOMBRE = ['#6fe3f2', '#4bb8d8', '#84a9e6', '#9fd67f', '#e0b25a'];
const SHOP_TINT_SOMBRE = {
  'GOLF SOKRA': CAT_SOMBRE[0], 'MANAR CITY': CAT_SOMBRE[1], 'TUNISIA MALL': CAT_SOMBRE[2],
  'AVENUE DE PARIS': CAT_SOMBRE[3], 'CENTRAL PARK': CAT_SOMBRE[4],
};
const GROUPE_TINT_SOMBRE = { solo: CAT_SOMBRE[0], grp2: CAT_SOMBRE[1], grp3: CAT_SOMBRE[2], grp4p: CAT_SOMBRE[3] };
const FAM_TINT_SOMBRE = ['#6fe3f2', '#4bb8d8', '#84a9e6', '#e0b25a', '#ff8a76', '#4ad98a'];
const AUTRES_TINT_SOMBRE = '#6d8794';

/* Echelles de la heatmap : elles partent du fond nuit au lieu du blanc, sinon
   les cases faibles brillent plus que les fortes. */
const TRAFIC_STOPS_SOMBRE = ['#11222d', '#1a4761', '#2a7ea4', '#48b6d8', '#93e6f6'];
const VENTES_STOPS_SOMBRE = ['#11241c', '#1a4832', '#2a8a55', '#46c47e', '#93e9b7'];
const CONVERSION_STOPS_SOMBRE = ['#2a1715', '#5c2622', '#a33c33', '#e06d57', '#ffb7a3'];

const ThemeGraphe = createContext(null);
/* Resolu a l'appel (pendant le rendu) : les paquets de theme sont construits
   plus bas, une fois toutes les constantes du module definies. */
const useTheme = () => useContext(ThemeGraphe) || THEME_CLAIR;

const nf = new Intl.NumberFormat('fr-FR');
const fmt = (v) => (v === null || v === undefined ? '—' : nf.format(Math.round(v)));
const fmt1 = (v) => (v === null || v === undefined ? '—' : nf.format(v));
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
/* 2026-08-04 -> 04/08/2026 */
const jourCourt = (s) => (s ? s.split('-').reverse().join('/') : '—');

const hexToRgb = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const mix = (h1, h2, t) => { const a = hexToRgb(h1), b = hexToRgb(h2); const c = a.map((v, i) => Math.round(v + (b[i] - v) * t)); return `rgb(${c[0]},${c[1]},${c[2]})`; };

/* Une echelle de couleur dediee par metrique de la heatmap : transparent/
   clair pour une valeur faible, teinte saturee pour une valeur elevee.
   Trafic en bleu, Ventes en vert, Conversion en rouge. */
const TRAFIC_STOPS = ['#eef4fc', '#bfdbfe', '#60a5fa', '#2563eb', '#1e3a8a'];
const VENTES_STOPS = ['#eefbf3', '#bbf0d1', '#4ade80', '#16a34a', '#14532d'];
const CONVERSION_STOPS = ['#fef2f2', '#fecaca', '#f87171', '#dc2626', '#7f1d1d'];
const STOPS_PAR_METRIQUE = { trafic: TRAFIC_STOPS, ventes: VENTES_STOPS, conversion: CONVERSION_STOPS };
const interpoler = (stops, t) => {
  const n = stops.length - 1;
  const echelle = Math.max(0, Math.min(1, t)) * n;
  const i = Math.min(n - 1, Math.floor(echelle));
  return mix(stops[i], stops[i + 1], echelle - i);
};

/* Seuils de conversion, regles depuis l'en-tete et memorises d'une session a
   l'autre. Au-dessus du seuil haut : vert. Entre les deux : jaune. En dessous
   du seuil bas : rouge.

   Un seuil unique pour tout le reseau ne rend pas justice a des boutiques qui
   n'ont ni le meme flux ni le meme potentiel : un centre commercial et une
   boutique de rue ne se jugent pas a la meme aune. La structure porte donc un
   seuil de reference (le reseau) et, facultativement, une EXCEPTION par
   boutique. Une boutique sans exception suit le reseau — c'est le cas par
   defaut, et il reste le plus courant. */
const SEUILS_DEFAUT = { min: 8, max: 12, parBoutique: {} };
const CLE_SEUILS = 'peak_seuils_conversion';

function lireSeuils() {
  try {
    const s = JSON.parse(localStorage.getItem(CLE_SEUILS));
    if (s && Number.isFinite(s.min) && Number.isFinite(s.max)) {
      // Reprise de l'ancien format, qui ne portait que { min, max }.
      return { ...s, parBoutique: s.parBoutique && typeof s.parBoutique === 'object' ? s.parBoutique : {} };
    }
  } catch (e) {
    /* valeur illisible : on repart des defauts */
  }
  return SEUILS_DEFAUT;
}

/* Tolere une saisie momentanement incoherente (min au-dessus de max) sans
   casser les couleurs pendant la frappe. */
const seuilsOrdonnes = (s) => ({
  min: Math.min(s.min, s.max),
  max: Math.max(s.min, s.max),
});

/* Seuils applicables a une boutique : son exception si elle en a une, sinon
   ceux du reseau. `boutique` nul = vue reseau. */
function seuilsDe(seuils, boutique) {
  const ex = boutique && seuils?.parBoutique?.[boutique];
  return ex && Number.isFinite(ex.min) && Number.isFinite(ex.max) ? ex : { min: seuils.min, max: seuils.max };
}

const couleurConv = (conv, seuils = SEUILS_DEFAUT) => {
  if (conv === null || conv === undefined) return COL.ink400;
  const { min, max } = seuilsOrdonnes(seuils);
  if (conv >= max) return COL.green;
  if (conv >= min) return COL.amber;
  return COL.red;
};

/* Reglage des seuils, applique soit au reseau, soit a une boutique precise.
   La cible suit la selection : tant qu'aucune boutique n'est choisie on regle
   le reseau ; des qu'une boutique est ouverte, on peut lui donner ses propres
   seuils sans toucher aux autres. */
function ReglageSeuils({ seuils, onChange, boutique }) {
  const { estMobile } = useEcran();
  const [cible, setCible] = useState('reseau');

  // Si l'on quitte une boutique, on ne peut plus regler « cette boutique ».
  const surBoutique = cible === 'boutique' && !!boutique;
  const courants = surBoutique ? seuilsDe(seuils, boutique) : { min: seuils.min, max: seuils.max };
  const exception = !!(boutique && seuils.parBoutique?.[boutique]);
  const nbExceptions = Object.keys(seuils.parBoutique || {}).length;

  const appliquer = (cle, v) => {
    if (!Number.isFinite(v)) return;
    if (surBoutique) {
      onChange({ ...seuils, parBoutique: { ...seuils.parBoutique, [boutique]: { ...courants, [cle]: v } } });
    } else {
      onChange({ ...seuils, [cle]: v });
    }
  };

  const retirerException = () => {
    const reste = { ...seuils.parBoutique };
    delete reste[boutique];
    onChange({ ...seuils, parBoutique: reste });
  };

  const champ = (cle) => (
    // `inputMode` decimal fait apparaitre le pave numerique sur telephone, et
    // 16px empeche iOS de zoomer a la mise au point.
    <input type="number" inputMode="decimal" min="0" max="100" step="0.5" value={courants[cle]}
      onChange={(e) => appliquer(cle, e.target.value === '' ? 0 : Number(e.target.value))}
      style={{
        width: estMobile ? 58 : 52, border: `1px solid ${COL.border}`, borderRadius: 6,
        padding: estMobile ? '7px 6px' : '4px 6px',
        fontSize: estMobile ? 16 : 12, fontWeight: 700, color: COL.ink900, textAlign: 'right',
        fontVariantNumeric: 'tabular-nums', background: COL.surface,
      }} />
  );

  const { min, max } = seuilsOrdonnes(courants);
  const pastille = (couleur, texte) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: COL.ink500 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: couleur }} />{texte}
    </span>
  );

  const ongletCible = (k, label, actif) => (
    <button key={k} onClick={() => setCible(k)} style={{
      border: 'none', borderRadius: 6, cursor: 'pointer',
      padding: estMobile ? '6px 10px' : '4px 9px', fontSize: estMobile ? 11.5 : 11, fontWeight: 600,
      maxWidth: estMobile ? 130 : 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      background: actif ? COL.surface : 'transparent', color: actif ? COL.ink900 : COL.ink500,
      boxShadow: actif ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
    }}>{label}</button>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: estMobile ? 8 : 10, flexWrap: 'wrap',
      background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 10,
      padding: estMobile ? '8px 10px' : '6px 12px', boxShadow: SHADOW,
      width: estMobile ? '100%' : 'auto', boxSizing: 'border-box',
    }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: COL.ink500, textTransform: 'uppercase', letterSpacing: '.06em' }}>
        Seuils conversion
      </span>

      {/* Le choix de la cible n'apparait que lorsqu'il a un sens, c'est-a-dire
          quand une boutique est ouverte. */}
      {boutique && (
        <span style={{ display: 'flex', gap: 3, background: COL.bg, padding: 3, borderRadius: 8, border: `1px solid ${COL.border}` }}>
          {ongletCible('reseau', 'Reseau', !surBoutique)}
          {ongletCible('boutique', boutique, surBoutique)}
        </span>
      )}

      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: COL.ink500 }}>
        bas {champ('min')} % · haut {champ('max')} %
      </span>

      {surBoutique && (exception
        ? (
          <button onClick={retirerException} title="Cette boutique suivra de nouveau les seuils du reseau"
            style={{
              border: `1px solid ${COL.border}`, borderRadius: 6, background: 'transparent', cursor: 'pointer',
              padding: estMobile ? '6px 9px' : '4px 8px', fontSize: 11, color: COL.ink500,
            }}>Revenir au reseau</button>
        )
        : <span style={{ fontSize: 11, color: COL.ink400 }}>suit le reseau</span>
      )}

      {!surBoutique && nbExceptions > 0 && (
        <span title={Object.keys(seuils.parBoutique).join(', ')}
          style={{ fontSize: 11, color: COL.amber, fontWeight: 600 }}>
          {nbExceptions} boutique{nbExceptions > 1 ? 's' : ''} a part
        </span>
      )}

      {/* Les trois pastilles de rappel disparaissent sur telephone : les
          valeurs sont juste au-dessus, elles ne font que doubler la barre. */}
      <span style={{ display: estMobile ? 'none' : 'inline-flex', alignItems: 'center', gap: 9 }}>
        {pastille(COL.red, `< ${min}`)}
        {pastille(COL.amber, `${min} – ${max}`)}
        {pastille(COL.green, `≥ ${max}`)}
      </span>
    </div>
  );
}

/* Le prix moyen affiche n'est pas le prix moyen de tous les articles vendus
   mais celui d'une paire de la famille de reference ci-dessous — c'est le
   coeur de metier. Consequence a garder en tete : cette famille represente
   environ 39 % du CA et 35 % des articles, le KPI ne parle donc que de cette
   part-la. Changer la constante suffit a basculer sur une autre famille. */
const FAMILLES_PRIX_MOYEN = ['CHAUSSURES', 'CHAUSSURES ANTA'];
const LIBELLE_PRIX_MOYEN = 'CHAUSSURES + ANTA';

/* Cumule les familles de reference presentes. Une famille absente de la
   periode est simplement ignoree : le prix moyen reste calcule sur celles
   qui ont vendu, jamais fausse par un zero. */
function agregatFamillesReference(familles) {
  let ca = 0;
  let qte = 0;
  (familles || []).forEach((f) => {
    if (!FAMILLES_PRIX_MOYEN.includes(f.famille)) return;
    if (f.ca === null || f.ca === undefined || !f.qte) return;
    ca += f.ca;
    qte += f.qte;
  });
  return qte > 0 ? { ca, qte, prix_moyen: Math.round((ca / qte) * 100) / 100 } : null;
}

function prixMoyenReference(familles) {
  const a = agregatFamillesReference(familles);
  return a ? a.prix_moyen : null;
}

/* Bascule "vrais clients" (staff exclu) / "tous les visiteurs" (staff inclus) :
   recalcule client base, groupes d'achat et conversion sur la meme base. */
function chiffres(o, base) {
  // Le staff est TOUJOURS exclu, dans les deux modes. La bascule ne change
  // que l'unite de comptage : par groupe d'achat (vrais) ou par personne (tous).
  const clients = base === 'tous' ? (o.vrais_clients || 0) : (o.portefeuilles_clients || 0);
  const portefeuilles = o.portefeuilles_clients || 0; // "Groupes d'achat" reste toujours base sur les groupes
  const taux_conversion = clients ? Math.round(((o.tickets || 0) / clients) * 1000) / 10 : null;
  const ca_par_visiteur = clients ? Math.round(((o.ca || 0) / clients) * 100) / 100 : null;
  return { clients, portefeuilles, taux_conversion, ca_par_visiteur };
}

function FlecheComparaison({ valeur, reference }) {
  if (valeur === null || valeur === undefined || !reference) return null;
  const positif = valeur >= reference;
  return <span style={{ color: positif ? COL.green : COL.red, fontWeight: 700, fontSize: 10 }}>{positif ? '▲' : '▼'}</span>;
}

function ToggleBase({ valeur, onChange }) {
  const options = [{ k: 'vrais', label: 'Vrais clients' }, { k: 'tous', label: 'Tous les visiteurs' }];
  return (
    <div style={{ display: 'flex', gap: 4, background: COL.bg, padding: 3, borderRadius: 8, border: `1px solid ${COL.border}` }}>
      {options.map((o) => (
        <button key={o.k} onClick={() => onChange(o.k)} style={{
          border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          background: valeur === o.k ? COL.surface : 'transparent', color: valeur === o.k ? COL.ink900 : COL.ink500,
          boxShadow: valeur === o.k ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
        }}>{o.label}</button>
      ))}
    </div>
  );
}

/* --- Tendance : le dernier jour COMPLET face aux precedents ---------------
   Le badge comparait le dernier jour de l'historique a la moyenne des
   precedents. Or ce dernier jour est la journee EN COURS, et les ventes ne
   remontent de Fastmag que trois fois par jour : une journee a moitie
   importee, mise face a des journees entieres, affichait -45 %, -64 %,
   -83 %... sur les cinq boutiques a la fois. Cinq etats d'import, et non cinq
   effondrements.

   On ecarte donc la journee commerciale en cours. Elle court de 9h a 9h le
   lendemain : avant 9h du matin, c'est encore celle de la veille. */
function jourCommercialCourant() {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  if (n.getHours() < 9) d.setDate(d.getDate() - 1);
  const p = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const joursComplets = (historique) => {
  const enCours = jourCommercialCourant();
  return (historique || []).filter((h) => h && h.jour !== enCours);
};

/* Renvoie de quoi EXPLIQUER le pourcentage, pas seulement le pourcentage :
   sans ses deux termes, « 45 % » ne veut rien dire pour qui le lit. */
function tendance(historique) {
  const tous = historique || [];
  const complets = joursComplets(tous);
  // On note ce qu'on a REELLEMENT ecarte, pour que l'infobulle le dise au lieu
  // de l'annoncer d'office : en consultant une date passee, aucune journee
  // n'est en cours, et affirmer le contraire est un mensonge de plus.
  const enCoursEcartee = complets.length < tous.length;
  // Un jour a zero n'est pas un mauvais jour, c'est une absence de mesure
  // (boutique fermee, import manque). L'inclure tirerait la moyenne vers le
  // bas et rendrait les jours suivants faussement verts.
  const jours = complets.filter((h) => h.ca > 0);
  const zerosEcartes = complets.length - jours.length;
  if (jours.length < 2) return null;
  const dernier = jours[jours.length - 1];
  const precedents = jours.slice(0, -1);
  const moyenne = precedents.reduce((s, h) => s + h.ca, 0) / precedents.length;
  if (!moyenne) return null;
  return {
    valeur: Math.round(((dernier.ca - moyenne) / moyenne) * 100),
    jour: dernier.jour,
    ca: dernier.ca,
    moyenne,
    nb: precedents.length,
    enCoursEcartee,
    zerosEcartes,
  };
}

function Sparkline({ valeurs, couleur, largeur = 68, hauteur = 26 }) {
  const propres = (valeurs || []).map((v) => v ?? 0);
  if (propres.length < 2) return <div style={{ width: largeur, height: hauteur }} />;
  const min = Math.min(...propres), max = Math.max(...propres, min + 1);
  const pas = largeur / (propres.length - 1);
  const pts = propres.map((v, i) => `${(i * pas).toFixed(1)},${(hauteur - ((v - min) / (max - min || 1)) * hauteur).toFixed(1)}`).join(' ');
  return (
    <svg width={largeur} height={hauteur} style={{ display: 'block', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={couleur} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TendanceBadge({ valeur }) {
  const { COL } = useTheme();
  if (!valeur) return null;
  const { valeur: v, jour, ca, moyenne, nb, enCoursEcartee, zerosEcartes } = valeur;
  const positif = v >= 0;
  // Un pourcentage sans ses deux termes ne se verifie pas. L'infobulle donne le
  // jour compare, son chiffre et la moyenne en face — puis ce qui a ete ecarte,
  // et SEULEMENT si quelque chose l'a ete.
  const lignes = [
    `${jourCourt(jour)} : ${fmt(ca)} DT`,
    `Moyenne des ${nb} jours precedents : ${fmt(moyenne)} DT`,
  ];
  if (enCoursEcartee) {
    lignes.push('', 'La journee en cours est ecartee : ses ventes ne sont pas',
      'toutes remontees, elle paraitrait en chute face a des',
      'journees entieres.');
  }
  if (zerosEcartes) {
    lignes.push('', `${zerosEcartes} jour${zerosEcartes > 1 ? 's' : ''} sans aucune vente ecarte${zerosEcartes > 1 ? 's' : ''} du calcul`,
      '(absence de mesure, et non mauvaise journee).');
  }
  return (
    <span
      title={lignes.join('\n')}
      style={{
        fontSize: 11, fontWeight: 700, color: positif ? COL.green : COL.red,
        display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'help',
      }}>
      {positif ? '▲' : '▼'} {Math.abs(v)}%
    </span>
  );
}

/* --- Petit bouton "i" pose DANS UN COIN d'un camembert (jamais sur l'anneau
   lui-meme) qui ouvre un panneau de stats flottant au clic. Separe expres du
   camembert : la ou les parts ont deja leur propre onClick (selection de
   boutique), un bouton dedie evite tout conflit de clic. Purement local /
   experimental. */
function BoutonStatsFlottantes({ children, position }) {
  const { COL, SHADOW, SHADOW_HOVER } = useTheme();
  const { estMobile } = useEcran();
  const [ouvert, setOuvert] = useState(false);
  // Cible elargie au doigt : 20 pixels sont sous le minimum touchable.
  const taille = estMobile ? 28 : 20;
  const pos = position || { top: 2, right: 2 };
  return (
    <div style={{ position: 'absolute', ...pos, zIndex: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOuvert((v) => !v); }}
        title="Plus de statistiques" aria-expanded={ouvert}
        style={{
          width: taille, height: taille, borderRadius: '50%', cursor: 'pointer', padding: 0,
          border: `1px solid ${ouvert ? COL.ink900 : COL.border}`,
          background: ouvert ? COL.ink900 : COL.surface,
          color: ouvert ? COL.surface : COL.ink500,
          fontSize: estMobile ? 13 : 11, fontWeight: 700, lineHeight: 1,
          boxShadow: SHADOW, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >i</button>
      {ouvert && (
        <div style={{
          position: 'absolute', top: taille + 6, right: 0, width: estMobile ? 190 : 210,
          background: COL.ink900, color: COL.surface, borderRadius: 10,
          boxShadow: SHADOW_HOVER, padding: '11px 13px', fontSize: 11.5, lineHeight: 1.6,
        }}>
          <button onClick={(e) => { e.stopPropagation(); setOuvert(false); }}
            style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', color: COL.surface, opacity: .6, cursor: 'pointer', fontSize: 13, padding: 0 }}
          >×</button>
          {children}
        </div>
      )}
    </div>
  );
}

function auditer(data, seuils, base) {
  const alertes = [];
  if (!data) return alertes;
  const btq = data.boutiques || [];

  /* Alerte de performance : les boutiques sous LEUR seuil bas.
     Elle vient en premier parce que c'est la seule sur laquelle on peut agir
     le jour meme — les autres signalent un probleme de donnees.
     Chaque boutique est jugee a l'aune de ses propres exigences : une boutique
     dotee d'une exception ne doit pas etre alertee sur le seuil du reseau. */
  if (seuils) {
    const sous = btq
      .map((b) => {
        const conv = chiffres(b, base).taux_conversion;
        const { min } = seuilsOrdonnes(seuilsDe(seuils, b.boutique));
        return conv === null || conv === undefined ? null : { nom: b.boutique, conv, min, ecart: min - conv };
      })
      .filter((x) => x && x.ecart > 0)
      // Le plus loin du seuil en premier : c'est par la qu'on commence.
      .sort((a, b) => b.ecart - a.ecart);

    if (sous.length) {
      alertes.push({
        niveau: 'error',
        titre: sous.length > 1 ? `${sous.length} boutiques sous leur seuil` : 'Une boutique sous son seuil',
        texte: sous.map((s) => `${s.nom} ${s.conv}% (seuil ${s.min}%)`).join(' · '),
      });
    }
  }

  const sansTrafic = btq.filter((b) => !b.portefeuilles_clients);
  const avecVentes = btq.filter((b) => b.tickets > 0);
  if (sansTrafic.length && avecVentes.length) {
    alertes.push({ niveau: 'warn', texte: `Trafic camera absent pour ${sansTrafic.length} boutique(s) : ${sansTrafic.map((b) => b.boutique).join(', ')}. Conversion non calculee pour ces boutiques.` });
  }
  const aberrants = btq.filter((b) => b.taux_conversion !== null && b.taux_conversion > 40);
  if (aberrants.length) {
    alertes.push({ niveau: 'error', texte: `Conversion anormalement haute sur ${aberrants.map((b) => `${b.boutique} (${b.taux_conversion}%)`).join(', ')}. Comptage probablement interrompu ce jour-la.` });
  }
  if (!data.global?.tickets) {
    alertes.push({ niveau: 'info', texte: "Aucune vente importee sur cette periode. L'import caisse a lieu a 7h15, 14h15 et 22h15." });
  }
  return alertes;
}

function Eyebrow({ children }) {
  const { COL } = useTheme();
  return <div style={{ fontSize: 11, fontWeight: 600, color: COL.ink500, textTransform: 'uppercase', letterSpacing: '.08em' }}>{children}</div>;
}

function Bloc({ titre, sous, children, action, style }) {
  const { COL, SHADOW } = useTheme();
  const { estMobile } = useEcran();
  return (
    <section style={{
      background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 12,
      // Sur telephone, 20px de marge de chaque cote amputent la largeur utile
      // du graphe de pres d'un sixieme.
      padding: estMobile ? 12 : 20, boxShadow: SHADOW, minWidth: 0, ...style,
    }}>
      {/* L'en-tete passe en colonne sur telephone : titre et selecteur cote a
          cote y deviennent illisibles tous les deux. */}
      <div style={{
        display: 'flex', flexDirection: estMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: estMobile ? 'stretch' : 'flex-start',
        marginBottom: estMobile ? 12 : 16, gap: estMobile ? 8 : 12,
      }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: estMobile ? 13 : 14, fontWeight: 700, color: COL.ink900 }}>{titre}</h3>
          {sous && <p style={{ margin: '3px 0 0', fontSize: estMobile ? 11.5 : 12.5, color: COL.ink500, lineHeight: 1.45 }}>{sous}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, unite, sub, hero, trend, sparkline, sparklineCouleur, couleurValeur, periode, requete, fige }) {
  const { COL, SHADOW } = useTheme();
  return (
    <div style={{ position: 'relative', background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 12, padding: hero ? '20px 22px' : '14px 16px', boxShadow: SHADOW }}>
      {!fige && periode && (
        <BoutonEpingler position={{ top: 6, right: 6 }} fabriquer={() => ({
          kind: 'kpi', requete, titre: label, periode,
          data: { label, value, unite, sub, trend, sparkline, sparklineCouleur, couleurValeur },
        })} />
      )}
      {/* La place a droite est reservee au bouton d'epinglage, sinon il
          recouvrirait le badge de tendance. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingRight: !fige && periode ? 22 : 0 }}>
        <Eyebrow>{label}</Eyebrow>
        {trend !== undefined && <TendanceBadge valeur={trend} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: hero ? 32 : 22, fontWeight: 700, lineHeight: 1.15, marginTop: 6, color: couleurValeur || COL.ink900, fontVariantNumeric: 'tabular-nums' }}>
          {value}{unite && <span style={{ fontSize: hero ? 15 : 12, color: COL.ink400, marginLeft: 5, fontWeight: 500 }}>{unite}</span>}
        </div>
        {sparkline && <Sparkline valeurs={sparkline} couleur={sparklineCouleur || COL.accent} />}
      </div>
      {sub && <div style={{ fontSize: 12, color: COL.ink500, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

/* --- Carte boutique premium : statut semantique + tendance + sparkline -- */
function CarteBoutique({ b, historique, actif, onClick, base, seuils }) {
  // La journee en cours est retiree de la courbe COMME du badge : sans cela
  // la sparkline plongerait en fin de trace pour la meme mauvaise raison.
  const complets = joursComplets(historique);
  const serieCA = complets.map((h) => h.ca);
  const tend = tendance(complets);
  const vc = chiffres(b, base);
  const prixChaussures = prixMoyenReference(b.familles);
  // Chaque carte se juge a l'aune de SA boutique : une boutique dotee d'une
  // exception ne doit pas etre coloree avec le seuil du reseau.
  const statut = couleurConv(vc.taux_conversion, seuilsDe(seuils, b.boutique));
  return (
    <button onClick={onClick} aria-pressed={actif} style={{
      textAlign: 'left', cursor: 'pointer', width: '100%', font: 'inherit', color: 'inherit',
      background: COL.surface, borderRadius: 12, padding: '16px 18px',
      border: `1.5px solid ${actif ? statut : COL.border}`,
      boxShadow: actif ? `0 0 0 3px ${statut}1f, ${SHADOW_HOVER}` : SHADOW,
      transition: 'box-shadow .15s, border-color .15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statut, flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.ink900, flex: 1 }}>{b.boutique}</span>
        <TendanceBadge valeur={tend} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 10 }}>
        <div style={{ fontSize: 25, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
          {fmt(b.ca)}<span style={{ fontSize: 11, color: COL.ink400, fontWeight: 500 }}> DT</span>
        </div>
        <Sparkline valeurs={serieCA} couleur={statut} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COL.border}`, fontSize: 11.5, flexWrap: 'wrap' }}>
        <span><b style={{ color: statut }}>{pct(vc.taux_conversion)}</b> <span style={{ color: COL.ink500 }}>conv.</span></span>
        {/* Regle voulue par l'utilisateur : seul le prix moyen porte une
            fleche, comparee au panier moyen de la meme boutique (vert si
            superieur ou egal). A savoir : panier = prix x articles par ticket,
            donc la fleche est rouge tant que l'on vend plus d'un article par
            ticket. Ce n'est pas un bug. */}
        <span style={{ color: COL.ink500 }}>{fmt(b.panier_moyen)} DT panier</span>
        <span style={{ color: COL.ink500 }}>{fmt(prixChaussures)} DT prix <FlecheComparaison valeur={prixChaussures} reference={b.panier_moyen} /></span>
        <span style={{ color: COL.ink500 }}>{fmt(vc.clients)} {base === 'tous' ? 'visiteurs' : 'vrais clients'}</span>
      </div>
    </button>
  );
}

/* --- Cercle de repartition (donut cliquable) ---------------------------- */
function CercleRepartition({ data, total, uniteTotal, selection, onSelect, periode, titrePlan, requete, fige }) {
  const { COL, tintOf } = useTheme();
  const triees = [...data].sort((a, b) => b.valeur - a.valeur);
  const tete = triees[0];
  const queue = triees[triees.length - 1];
  const pctDe = (v) => (total ? Math.round((v / total) * 100) : 0);
  return (
    <div>
      <div style={{ position: 'relative', height: 190 }}>
        <CadreGraphe hauteur={190}>
          {(l, ht) => (
          <PieChart width={l} height={ht}>
            <Pie isAnimationActive={!fige} data={data} dataKey="valeur" nameKey="nom" cx="50%" cy="50%" innerRadius={54} outerRadius={80} paddingAngle={2} stroke="none">
              {data.map((d) => (
                <Cell key={d.nom} fill={tintOf(d.nom)}
                  stroke={selection === d.nom ? COL.ink900 : COL.surface}
                  strokeWidth={selection === d.nom ? 3 : 1}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelect(selection === d.nom ? null : d.nom)} />
              ))}
            </Pie>
            <Tooltip formatter={(v, n) => [nf.format(v), n]}
              contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} />
          </PieChart>
          )}
        </CadreGraphe>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 19, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</div>
          <div style={{ fontSize: 10.5, color: COL.ink500 }}>{uniteTotal}</div>
        </div>
        {tete && queue && tete !== queue && (
          <BoutonStatsFlottantes>
            <div><b>{tete.nom}</b> en tete avec {pctDe(tete.valeur)}%</div>
            <div style={{ marginTop: 4 }}><b>{queue.nom}</b> en dernier avec {pctDe(queue.valeur)}%</div>
            <div style={{ marginTop: 4, opacity: .75 }}>Ecart : {pctDe(tete.valeur) - pctDe(queue.valeur)} points</div>
          </BoutonStatsFlottantes>
        )}
        {!fige && (
          <BoutonEpingler fabriquer={() => ({
            kind: 'repartition', requete, titre: titrePlan, periode,
            data: { data, total, uniteTotal },
          })} />
        )}
      </div>
      <div style={{ display: 'grid', gap: 5, marginTop: 10 }}>
        {data.map((d) => (
          <button key={d.nom} onClick={() => onSelect(selection === d.nom ? null : d.nom)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: selection === d.nom ? COL.bgPale : 'transparent',
            border: 'none', padding: '4px 6px', cursor: 'pointer', textAlign: 'left', borderRadius: 6,
          }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: tintOf(d.nom), flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: COL.ink900, fontWeight: selection === d.nom ? 700 : 500, flex: 1 }}>{d.nom}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums' }}>
              {total ? Math.round((d.valeur / total) * 100) : 0}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* --- Camembert composition des groupes (contextuel a la selection et a
   la bascule vrais clients / tous visiteurs) -----------------------------
   Le detail exact "quels groupes etaient du staff" n'existe pas en base
   (seul le total staff par groupe est connu) : en mode "vrais clients",
   on applique donc a chaque taille de groupe le meme ratio d'exclusion
   que celui deja valide sur le total (portefeuilles_clients/portefeuilles). */
const TAILLE_GROUPE = { solo: 1, grp2: 2, grp3: 3, grp4p: 4 };
function CamembertGroupes({ groupes, ratio, base, titre, periode, titrePlan, requete, fige }) {
  const { COL, GROUPE_TINT } = useTheme();
  const groupesParTaille = Object.entries(groupes || {}).map(([k, v]) => ({ k, n: Math.round((v || 0) * ratio) }));
  const data = groupesParTaille.map(({ k, n }) => ({
    nom: GROUPE_LABEL[k], k, valeur: base === 'tous' ? n * TAILLE_GROUPE[k] : n,
  }));
  const total = data.reduce((s, d) => s + d.valeur, 0);
  // Meme gabarit que les autres cartes de la rangee : anneau centre sur le
  // total, legende dessous. Sans quoi cette carte parait plus petite et
  // casse l'alignement de la rangee.
  const uniteTotal = base === 'tous' ? 'visiteurs' : "groupes d'achat";
  const nbGroupes = groupesParTaille.reduce((s, d) => s + d.n, 0);
  const nbPersonnes = groupesParTaille.reduce((s, d) => s + d.n * TAILLE_GROUPE[d.k], 0);
  const nbSolo = groupesParTaille.find((d) => d.k === 'solo')?.n || 0;
  const tailleMoyenne = nbGroupes ? Math.round((nbPersonnes / nbGroupes) * 100) / 100 : null;
  return (
    <div>
      <div style={{ position: 'relative', height: 190 }}>
        <CadreGraphe hauteur={190}>
          {(l, ht) => (
          <PieChart width={l} height={ht}>
            <Pie isAnimationActive={!fige} data={data} dataKey="valeur" nameKey="nom" cx="50%" cy="50%" innerRadius={54} outerRadius={80} paddingAngle={2} stroke="none">
              {data.map((d) => <Cell key={d.k} fill={GROUPE_TINT[d.k]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [nf.format(v), n]}
              contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} />
          </PieChart>
          )}
        </CadreGraphe>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 19, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</div>
          <div style={{ fontSize: 10.5, color: COL.ink500 }}>{uniteTotal}</div>
        </div>
        {nbGroupes > 0 && (
          <BoutonStatsFlottantes>
            <div>Taille moyenne du groupe : <b>{fmt1(tailleMoyenne)}</b> pers.</div>
            <div style={{ marginTop: 4 }}><b>{Math.round((nbSolo / nbGroupes) * 100)}%</b> des groupes viennent seuls</div>
            <div style={{ marginTop: 4, opacity: .75 }}>{fmt(nbGroupes)} groupes pour {fmt(nbPersonnes)} personnes</div>
          </BoutonStatsFlottantes>
        )}
        {!fige && (
          <BoutonEpingler fabriquer={() => ({
            kind: 'groupes', requete, titre: titrePlan, periode,
            data: { groupes, ratio, base },
          })} />
        )}
      </div>
      <div style={{ display: 'grid', gap: 5, marginTop: 10 }}>
        {data.map((d) => (
          <div key={d.k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px' }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: GROUPE_TINT[d.k], flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: COL.ink900, flex: 1 }}>{d.nom}</span>
            <span style={{ fontSize: 12, color: COL.ink500, fontVariantNumeric: 'tabular-nums' }}>{fmt(d.valeur)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>
              {total ? Math.round((d.valeur / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --- Frequentation cumulee : toutes boutiques, toujours affichee -------- */
function FrequentationCumulee({ hourlyByShop, boutiques, base, periode, requete, fige }) {
  const { COL, tintOf } = useTheme();
  const { estMobile } = useEcran();
  const champ = base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients';
  const heures = useMemo(() => {
    const set = new Set();
    Object.values(hourlyByShop || {}).forEach((arr) => arr.forEach((h) => set.add(h.heure)));
    return [...set].sort((a, b) => a - b);
  }, [hourlyByShop]);

  const donnees = heures.map((h) => {
    const ligne = { heure: h };
    boutiques.forEach((b) => {
      const arr = hourlyByShop?.[b.boutique] || [];
      ligne[b.boutique] = arr.filter((x) => x.heure <= h).reduce((s, x) => {
        const v = x[champ] !== undefined ? x[champ] : x.portefeuilles_clients;
        return s + (v || 0);
      }, 0);
    });
    return ligne;
  });

  // Hauteur donnee en pixels a ResponsiveContainer, jamais en pourcentage :
  // un pourcentage l'oblige a mesurer son parent, ce qui renvoie -1 tant que
  // la mise en page n'est pas resolue (conteneur flex defilable de la carte).
  const hauteur = fige ? 250 : 260;
  const graphe = (
    // Cinq courbes sur douze heures : sans largeur minimale, elles se
    // superposent au point d'etre indechiffrables sur un telephone.
    <CadreGraphe hauteur={hauteur} largeurMin={estMobile ? 540 : 0}>
      {(l, h) => (
        <LineChart width={l} height={h} data={donnees}>
          <CartesianGrid stroke={COL.border} vertical={false} />
          <XAxis dataKey="heure" tick={{ fill: COL.ink400, fontSize: 11 }} tickFormatter={(x) => `${x}h`} axisLine={{ stroke: COL.border }} tickLine={false} />
          <YAxis tick={{ fill: COL.ink400, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} labelFormatter={(x) => `${x}h`} />
          <Legend wrapperStyle={{ fontSize: 11.5 }} />
          {boutiques.map((b) => (
            <Line isAnimationActive={!fige} key={b.boutique} type="monotone" dataKey={b.boutique} name={b.boutique} stroke={tintOf(b.boutique)} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      )}
    </CadreGraphe>
  );

  if (fige) return graphe;
  return (
    <Bloc titre="Frequentation cumulee" sous="Entrees clients au fil de la journee, boutique par boutique"
      action={<BoutonEpingler enFlux fabriquer={() => ({
        kind: 'frequentation', requete, titre: 'Frequentation cumulee', periode, taille: 'grand',
        data: { hourlyByShop, boutiques, base },
      })} />}>
      {graphe}
    </Bloc>
  );
}

/* --- Heatmap boutique x heure -------------------------------------------- */
function Heatmap({ hourlyByShop, boutiques, selection, base, periode, requete, fige }) {
  const { COL, CAT, STOPS_PAR_METRIQUE } = useTheme();
  const { estMobile } = useEcran();
  const [metrique, setMetrique] = useState('trafic');
  const options = [
    { k: 'trafic', label: 'Trafic', unite: base === 'tous' ? 'visiteurs' : 'groupes', champ: base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients' },
    { k: 'ventes', label: 'Ventes', unite: 'DT', champ: 'ca' },
    { k: 'conversion', label: 'Conversion', unite: '%', champ: 'taux_conversion' },
  ];
  const opt = options.find((o) => o.k === metrique);
  const champClients = base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients';
  const valeurCase = (slot) => {
    if (!slot) return undefined;
    if (metrique === 'conversion') {
      const cl = slot[champClients];
      return cl ? Math.round((slot.tickets / cl) * 1000) / 10 : null;
    }
    return slot[opt.champ];
  };
  const heures = useMemo(() => {
    const set = new Set();
    Object.values(hourlyByShop || {}).forEach((arr) => arr.forEach((h) => set.add(h.heure)));
    return [...set].sort((a, b) => a - b);
  }, [hourlyByShop]);
  const lignes = (boutiques || []).map((b) => {
    const parHeure = {};
    (hourlyByShop?.[b.boutique] || []).forEach((h) => { parHeure[h.heure] = h; });
    return { boutique: b.boutique, parHeure };
  });
  const toutesValeurs = lignes.flatMap((l) => heures.map((h) => valeurCase(l.parHeure[h])).filter((v) => v !== undefined && v !== null));
  const max = toutesValeurs.length ? Math.max(...toutesValeurs, 0.0001) : 1;
  const stopsActuels = STOPS_PAR_METRIQUE[metrique] || STOPS_PAR_METRIQUE.trafic;
  const couleurCase = (v) => {
    if (v === undefined || v === null) return COL.bg;
    const t = Math.sqrt(Math.max(0, Math.min(1, v / max)));
    return interpoler(stopsActuels, t);
  };
  const luminanceDe = (rgb) => {
    const m = rgb.match(/\d+/g);
    if (!m) return 1;
    const [r, gc, b] = m.map(Number);
    return (0.2126 * r + 0.7152 * gc + 0.0722 * b) / 255;
  };
  const texteCase = (v) => (v === undefined || v === null ? COL.ink400 : luminanceDe(couleurCase(v)) < 0.52 ? '#ffffff' : COL.ink700);

  const selecteur = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {!fige && (
        <BoutonEpingler enFlux fabriquer={() => ({
          kind: 'heatmap', requete, titre: 'Intensite par heure', periode, taille: 'grand',
          data: { hourlyByShop, boutiques, base },
        })} />
      )}
      <div style={{ display: 'flex', gap: 4, background: COL.bg, padding: 3, borderRadius: 8, border: `1px solid ${COL.border}` }}>
        {options.map((o) => (
          <button key={o.k} onClick={() => setMetrique(o.k)} style={{
            border: 'none', borderRadius: 6, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: metrique === o.k ? COL.surface : 'transparent', color: metrique === o.k ? COL.ink900 : COL.ink500,
            boxShadow: metrique === o.k ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
          }}>{o.label}</button>
        ))}
      </div>
    </div>
  );

  const contenu = (
    <>
      {fige && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>{selecteur}</div>}
      {/* La grille garde des cases lisibles et defile horizontalement plutot
          que de se tasser : sur telephone, la colonne des noms est reduite et
          les noms abreges a leur premier mot. */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `${estMobile ? 62 : 120}px repeat(${heures.length},minmax(${estMobile ? 24 : 26}px,1fr))`,
          gap: 3, minWidth: (estMobile ? 62 : 120) + heures.length * (estMobile ? 26 : 28),
        }}>
          <div />
          {heures.map((h) => <div key={h} style={{ fontSize: 10, color: COL.ink400, textAlign: 'center', paddingBottom: 2 }}>{h}h</div>)}
          {lignes.map((l) => (
            <React.Fragment key={l.boutique}>
              <div title={l.boutique} style={{
                fontSize: estMobile ? 10 : 12, color: COL.ink700, fontWeight: selection === l.boutique ? 700 : 500,
                display: 'flex', alignItems: 'center', paddingRight: 6, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {l.boutique.split(' ').slice(0, estMobile ? 1 : 2).join(' ')}
              </div>
              {heures.map((h) => {
                const v = valeurCase(l.parHeure[h]);
                return (
                  <div key={h} title={`${l.boutique} — ${h}h : ${v !== undefined && v !== null ? fmt1(v) : 'aucune donnee'} ${opt.unite}`} style={{
                    background: couleurCase(v), color: texteCase(v), borderRadius: 4, height: 26,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontVariantNumeric: 'tabular-nums',
                    border: selection === l.boutique ? `1.5px solid ${CAT[0]}` : '1px solid transparent',
                  }}>
                    {v !== undefined && v !== null ? Math.round(v) : ''}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
        <span style={{ fontSize: 10.5, color: COL.ink400 }}>Faible</span>
        <div style={{ flex: 1, maxWidth: 160, height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${stopsActuels.join(', ')})` }} />
        <span style={{ fontSize: 10.5, color: COL.ink400 }}>Fort</span>
      </div>
    </>
  );

  if (fige) return contenu;
  return (
    <Bloc titre="Intensite par heure" sous="Une case = une boutique a une heure donnee. Plus la teinte est foncee, plus la valeur est elevee."
      action={selecteur}>
      {contenu}
    </Bloc>
  );
}

/* --- Camembert des ventes par famille ------------------------------------
   Repond a "qu'est-ce qui se vend le plus ici ?" : part de chaque famille
   produit, pour la boutique selectionnee ou pour le reseau entier, sur la
   periode choisie. Au-dela de 6 familles le reste est regroupe en "Autres"
   pour que le camembert reste lisible.

   ATTENTION : la colonne `familles` de la caisse ne porte pas toujours les
   montants (certaines lignes n'ont que le nom, d'autres le nom + la quantite).
   Les parts sont donc calculees sur la portion detaillee, pas sur le CA total,
   et le taux de couverture est affiche sous le camembert. */
const FAM_TINT = ['#0f2e3d', '#2f6577', '#5c99a6', '#92620d', '#a42a1f', '#1c7a42'];
const AUTRES_TINT = '#9aa2af';
const MAX_PARTS = 6;

function CamembertFamilles({ familles, caTotal, articlesTotal, periode, titrePlan, requete, fige }) {
  const { COL, FAM_TINT, AUTRES_TINT } = useTheme();
  const [mesure, setMesure] = useState('ca');
  const champ = mesure === 'ca' ? 'ca' : 'qte';
  const unite = mesure === 'ca' ? 'DT' : 'article(s)';
  const reference = mesure === 'ca' ? caTotal : articlesTotal;

  // Les retours peuvent produire un CA negatif : un camembert ne sait pas les
  // representer, on ne garde donc que les familles a valeur positive.
  const positives = (familles || []).filter((f) => (f[champ] || 0) > 0);
  if (!positives.length) {
    return <div style={{ color: COL.ink400, fontSize: 12.5 }}>Aucun detail par famille sur cette periode.</div>;
  }

  const triees = [...positives].sort((a, b) => b[champ] - a[champ]);
  const tete = triees.slice(0, MAX_PARTS);
  const reste = triees.slice(MAX_PARTS);
  const data = tete.map((f, i) => ({ nom: f.famille, valeur: f[champ], couleur: FAM_TINT[i % FAM_TINT.length] }));
  if (reste.length) {
    data.push({ nom: `Autres (${reste.length})`, valeur: reste.reduce((s, f) => s + f[champ], 0), couleur: AUTRES_TINT });
  }

  const detaille = triees.reduce((s, f) => s + f[champ], 0);
  const premier = triees[0];
  const partPremier = Math.round((premier[champ] / detaille) * 100);
  const couverture = reference ? Math.round((detaille / reference) * 100) : null;
  const top3 = triees.slice(0, 3);
  const partTop3 = Math.round((top3.reduce((s, f) => s + f[champ], 0) / detaille) * 100);

  const boutonMesure = (k, label) => (
    <button key={k} onClick={() => setMesure(k)} style={{
      border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
      background: mesure === k ? COL.surface : 'transparent', color: mesure === k ? COL.ink900 : COL.ink500,
      boxShadow: mesure === k ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 3, background: COL.bg, padding: 3, borderRadius: 8, border: `1px solid ${COL.border}` }}>
          {boutonMesure('ca', 'Chiffre d’affaires')}
          {boutonMesure('qte', 'Quantites')}
        </div>
      </div>

      <div style={{ position: 'relative', height: 200 }}>
        <CadreGraphe hauteur={200}>
          {(l, ht) => (
          <PieChart width={l} height={ht}>
            <Pie isAnimationActive={!fige} data={data} dataKey="valeur" nameKey="nom" cx="50%" cy="50%" innerRadius={56} outerRadius={84} paddingAngle={2} stroke="none">
              {data.map((d) => <Cell key={d.nom} fill={d.couleur} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${nf.format(Math.round(v))} ${unite} · ${Math.round((v / detaille) * 100)}%`, n]}
              contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} />
          </PieChart>
          )}
        </CadreGraphe>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', padding: '0 46px' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{partPremier}%</div>
          <div style={{ fontSize: 10, color: COL.ink500, textAlign: 'center', marginTop: 3, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{premier.famille}</div>
        </div>
        <BoutonStatsFlottantes>
          <div>Top 3 familles = <b>{partTop3}%</b> {mesure === 'ca' ? 'du CA' : 'des articles'}</div>
          {reste.length > 0 && <div style={{ marginTop: 4 }}>{reste.length} autre(s) famille(s) regroupee(s) dans "Autres"</div>}
          {couverture !== null && <div style={{ marginTop: 4, opacity: .75 }}>Couverture des donnees : {couverture}%</div>}
        </BoutonStatsFlottantes>
        {!fige && (
          <BoutonEpingler fabriquer={() => ({
            kind: 'familles', requete, titre: titrePlan || 'Ventes par famille', periode,
            data: { familles, caTotal, articlesTotal },
          })} />
        )}
      </div>

      <div style={{ display: 'grid', gap: 4, marginTop: 12 }}>
        {data.map((d) => (
          <div key={d.nom} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: d.couleur, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: COL.ink900, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{d.nom}</span>
            <span style={{ fontSize: 12, color: COL.ink500, fontVariantNumeric: 'tabular-nums' }}>{fmt(d.valeur)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>
              {Math.round((d.valeur / detaille) * 100)}%
            </span>
          </div>
        ))}
      </div>

      {couverture !== null && couverture < 98 && (
        <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${COL.border}`, fontSize: 11, color: COL.ink500, lineHeight: 1.45 }}>
          Parts calculees sur {fmt(detaille)} {unite} detailles, soit {couverture}% du total
          ({fmt(reference)} {unite}) : la caisse ne ventile pas toutes les lignes par famille.
        </div>
      )}
    </div>
  );
}

/* --- Camembert des heures de vente ---------------------------------------
   Repond a "quand est-ce que je vends ?" pour la boutique selectionnee (ou
   le reseau entier) sur la periode choisie.

   Decoupe en tranches et non heure par heure : sur un mois les 15 heures
   d'ouverture pesent toutes entre 4% et 11%, un camembert a 15 parts serait
   illisible et ne montrerait aucun ecart. L'heure de pointe exacte est
   affichee au centre, ou la precision compte vraiment. */
const TRANCHES = [
  { k: 'matin',  label: 'Matin',          borne: 12, plage: 'avant 12h' },
  { k: 'midi',   label: 'Midi',           borne: 14, plage: '12h – 14h' },
  { k: 'apresm', label: 'Apres-midi',     borne: 17, plage: '14h – 17h' },
  { k: 'soiree', label: 'Soiree',         borne: 20, plage: '17h – 20h' },
  { k: 'fin',    label: 'Fin de journee', borne: 99, plage: 'a partir de 20h' },
];

/* La journee commerciale va de 9h a 3h du matin : les heures 0h-3h sont la
   fin de la soiree precedente, pas le petit matin. */
const heureCommerciale = (h) => (h <= 3 ? h + 24 : h);
const trancheDe = (h) => TRANCHES.find((t) => heureCommerciale(h) < t.borne) || TRANCHES[TRANCHES.length - 1];

function CamembertHeuresVentes({ horaire, cible, titre, sous, periode, requete, fige }) {
  const { COL, CAT } = useTheme();
  const [mesure, setMesure] = useState('ca');
  const unite = mesure === 'ca' ? 'DT' : 'ticket(s)';

  // `fige` = rendu dans le plan de travail : la carte porte deja son propre
  // cadre et son titre, on se passe du Bloc. Encadrer via une fonction plutot
  // qu'un composant defini ici : un composant recree a chaque rendu ferait
  // remonter tout le graphe (et le fait clignoter) au moindre clic.
  const encadrer = (contenu, action) => (fige
    ? <div>{contenu}</div>
    : <Bloc titre={titre} sous={sous} action={action}>{contenu}</Bloc>);

  const lignes = (horaire || []).filter((s) => (s[mesure] || 0) > 0);
  if (!lignes.length) {
    return encadrer(
      <div style={{ color: COL.ink400, fontSize: 12.5, padding: '12px 0' }}>Aucune vente sur cette periode.</div>
    );
  }

  const parTranche = {};
  lignes.forEach((s) => {
    const t = trancheDe(s.heure);
    parTranche[t.k] = (parTranche[t.k] || 0) + (s[mesure] || 0);
  });
  const data = TRANCHES.filter((t) => parTranche[t.k]).map((t, i) => ({
    nom: t.label, plage: t.plage, valeur: parTranche[t.k],
    couleur: CAT[TRANCHES.indexOf(t) % CAT.length],
  }));
  const total = data.reduce((s, d) => s + d.valeur, 0);

  const pointe = lignes.reduce((meilleur, s) => ((s[mesure] || 0) > (meilleur[mesure] || 0) ? s : meilleur), lignes[0]);
  const partPointe = Math.round((pointe[mesure] / total) * 100);
  const creuse = data.length > 1 ? [...data].sort((a, b) => a.valeur - b.valeur)[0] : null;

  const boutonMesure = (k, label) => (
    <button key={k} onClick={() => setMesure(k)} style={{
      border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
      background: mesure === k ? COL.surface : 'transparent', color: mesure === k ? COL.ink900 : COL.ink500,
      boxShadow: mesure === k ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
    }}>{label}</button>
  );

  // Le selecteur vit dans l'en-tete du bloc (comme la heatmap) : place au
  // dessus du graphe il decalerait l'anneau vers le bas et casserait
  // l'alignement avec les autres cartes de la rangee.
  const selecteur = (
    <div style={{ display: 'flex', gap: 3, background: COL.bg, padding: 3, borderRadius: 8, border: `1px solid ${COL.border}` }}>
      {boutonMesure('ca', 'CA')}
      {boutonMesure('tickets', 'Tickets')}
    </div>
  );

  return encadrer(
    <>
      {fige && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>{selecteur}</div>}
      <div style={{ position: 'relative', height: 190 }}>
        <CadreGraphe hauteur={190}>
          {(l, ht) => (
          <PieChart width={l} height={ht}>
            <Pie isAnimationActive={!fige} data={data} dataKey="valeur" nameKey="nom" cx="50%" cy="50%" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
              {data.map((d) => <Cell key={d.nom} fill={d.couleur} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${nf.format(Math.round(v))} ${unite} · ${Math.round((v / total) * 100)}%`, n]}
              contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} />
          </PieChart>
          )}
        </CadreGraphe>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 23, fontWeight: 700, color: COL.ink900, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{pointe.heure}h</div>
          <div style={{ fontSize: 10, color: COL.ink500, marginTop: 3, textAlign: 'center', lineHeight: 1.3 }}>
            heure de pointe<br />{partPointe}% des ventes
          </div>
        </div>
        {creuse && (
          <BoutonStatsFlottantes>
            <div>Periode la plus calme : <b>{creuse.nom}</b> <span style={{ opacity: .75 }}>({creuse.plage})</span></div>
            <div style={{ marginTop: 4 }}>{Math.round((creuse.valeur / total) * 100)}% des {unite === 'DT' ? 'ventes' : 'tickets'}</div>
            <div style={{ marginTop: 4, opacity: .75 }}>Contre {partPointe}% a l'heure de pointe</div>
          </BoutonStatsFlottantes>
        )}
        {!fige && (
          <BoutonEpingler fabriquer={() => ({
            kind: 'heures', requete, titre: `Heures de vente — ${cible}`, periode,
            data: { horaire, cible },
          })} />
        )}
      </div>

      <div style={{ display: 'grid', gap: 4, marginTop: 10 }}>
        {data.map((d) => (
          <div key={d.nom} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: d.couleur, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: COL.ink900, flex: 1, minWidth: 0 }}>
              {d.nom} <span style={{ color: COL.ink400, fontSize: 11 }}>{d.plage}</span>
            </span>
            <span style={{ fontSize: 12, color: COL.ink500, fontVariantNumeric: 'tabular-nums' }}>{fmt(d.valeur)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.ink900, fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>
              {Math.round((d.valeur / total) * 100)}%
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 9, paddingTop: 8, borderTop: `1px solid ${COL.border}`, fontSize: 11, color: COL.ink500 }}>
        {fmt(total)} {unite} sur la periode · {cible}
      </div>
    </>,
    selecteur
  );
}

/* --- Bloc famille : camembert (la forme) + tableau (les chiffres) -------- */
function BlocFamilles({ titre, sous, familles, caTotal, articlesTotal, periode, titrePlan, requete }) {
  const { estMobile } = useEcran();
  return (
    <Bloc titre={titre} sous={sous}>
      <div style={{ display: 'flex', gap: estMobile ? 16 : 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Largeur figee a 290px sur un ecran de 360px : le camembert debordait
            du bloc. En dessous du seuil, il prend simplement toute la largeur. */}
        <div style={{ width: estMobile ? '100%' : 290, flexShrink: 0, minWidth: 0 }}>
          <CamembertFamilles familles={familles} caTotal={caTotal} articlesTotal={articlesTotal}
            periode={periode} requete={requete} titrePlan={titrePlan} />
        </div>
        <div style={{ flex: estMobile ? '1 1 100%' : '1 1 340px', minWidth: 0 }}>
          <Familles familles={familles} />
        </div>
      </div>
    </Bloc>
  );
}

/* --- Tableau des familles produit ---------------------------------------- */
function Familles({ familles }) {
  if (!familles || !familles.length) return <div style={{ color: COL.ink400, fontSize: 13, padding: '12px 0' }}>Aucune vente enregistree sur cette periode.</div>;
  const caMax = Math.max(...familles.map((f) => f.ca || 0), 1);
  // Quatre colonnes chiffrees ne tiennent pas sous 360px : le tableau defile
  // horizontalement plutot que de tasser les nombres jusqu'a l'illisible.
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <table style={{ width: '100%', minWidth: 300, borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '0 8px 8px 0' }}><Eyebrow>Famille</Eyebrow></th>
          <th style={{ textAlign: 'right', padding: '0 8px 8px' }}><Eyebrow>Qte</Eyebrow></th>
          <th style={{ textAlign: 'right', padding: '0 8px 8px' }}><Eyebrow>CA</Eyebrow></th>
          <th style={{ textAlign: 'right', padding: '0 0 8px 8px' }}><Eyebrow>Prix moyen</Eyebrow></th>
        </tr>
      </thead>
      <tbody>
        {familles.map((f) => (
          <tr key={f.famille} style={{ borderTop: `1px solid ${COL.border}` }}>
            <td style={{ padding: '9px 8px 9px 0', color: COL.ink900, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 0, top: 5, bottom: 5, width: `${((f.ca || 0) / caMax) * 100}%`, background: COL.bgPale, borderRadius: 2 }} />
              <span style={{ position: 'relative' }}>{f.famille}</span>
            </td>
            <td style={{ textAlign: 'right', padding: '9px 8px', color: COL.ink500, fontVariantNumeric: 'tabular-nums' }}>{fmt(f.qte)}</td>
            <td style={{ textAlign: 'right', padding: '9px 8px', color: COL.ink900, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(f.ca)}</td>
            <td style={{ textAlign: 'right', padding: '9px 0 9px 8px', color: COL.ink500, fontVariantNumeric: 'tabular-nums' }}>{fmt1(f.prix_moyen)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/* --- Cadre des graphes denses --------------------------------------------
   Deux problemes traites ici.

   1. DIMENSIONS EXPLICITES. `ResponsiveContainer` de recharts se dimensionne
      en mesurant son parent. Sur mobile, cette mesure renvoyait zero et le
      graphe ne dessinait alors NI courbe NI axes — seule la legende, qui est
      du HTML et non du SVG, restait visible, d'ou de grands cadres blancs.
      On mesure donc nous-memes, une bonne fois, et on transmet une largeur et
      une hauteur en pixels au graphe : plus aucune mesure indirecte.

   2. LARGEUR MINIMALE. Un graphe a douze pas de temps et deux axes verticaux
      n'a plus qu'un tiers de sa largeur pour tracer sur un telephone. Plutot
      que de le tasser, on lui impose une largeur de confort et on fait defiler
      horizontalement — comme pour la heatmap et le tableau des familles.

   `children` est une fonction qui recoit (largeur, hauteur). */
function CadreGraphe({ hauteur, largeurMin = 0, children }) {
  const boite = useRef(null);
  const [dispo, setDispo] = useState(0);

  useEffect(() => {
    const el = boite.current;
    if (!el) return undefined;
    const mesurer = () => setDispo(el.clientWidth);
    mesurer();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', mesurer);
      return () => window.removeEventListener('resize', mesurer);
    }
    const ro = new ResizeObserver(mesurer);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const largeur = Math.max(largeurMin, dispo);
  return (
    <div ref={boite} style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ width: largeur || '100%', height: hauteur }}>
        {/* Tant que la largeur reelle n'est pas connue, on ne dessine rien :
            un graphe rendu a zero pixel reste blanc meme apres coup. */}
        {largeur > 0 ? children(largeur, hauteur) : null}
      </div>
    </div>
  );
}

/* --- Trafic et ventes par heure, pour une boutique -----------------------
   Extrait du detail boutique pour pouvoir etre epingle tel quel sur le plan
   de travail. `fige` retire le cadre : la carte du plan porte deja le sien. */
function GrapheTraficHeure({ horaire, base, boutique, periode, requete, fige }) {
  const { COL, CAT } = useTheme();
  const { estMobile } = useEcran();
  const axisStyle = { fill: COL.ink400, fontSize: 11 };
  const champTrafic = base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients';
  const hauteur = fige ? 250 : (estMobile ? 250 : 230);

  const graphe = (
    <CadreGraphe hauteur={hauteur} largeurMin={estMobile ? 540 : 0}>
      {(l, h) => (
        <ComposedChart width={l} height={h} data={horaire || []}>
          <CartesianGrid stroke={COL.border} vertical={false} />
          <XAxis dataKey="heure" tick={axisStyle} tickFormatter={(x) => `${x}h`} axisLine={{ stroke: COL.border }} tickLine={false} />
          <YAxis yAxisId="l" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis yAxisId="r" orientation="right" tick={axisStyle} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} labelFormatter={(x) => `${x}h`} />
          <Legend wrapperStyle={{ fontSize: 11.5 }} />
          <Bar isAnimationActive={!fige} yAxisId="l" dataKey={champTrafic} name={base === 'tous' ? 'Groupes entres (tous)' : 'Groupes entres (clients)'} fill={CAT[3]} radius={[3, 3, 0, 0]} />
          <Line isAnimationActive={!fige} yAxisId="r" type="monotone" dataKey="tickets" name="Tickets" stroke={CAT[0]} strokeWidth={2} dot={false} />
        </ComposedChart>
      )}
    </CadreGraphe>
  );

  if (fige) return graphe;
  return (
    <Bloc titre="Trafic et ventes par heure"
      action={<BoutonEpingler enFlux fabriquer={() => ({
        kind: 'trafic_heure', requete, titre: `Trafic et ventes par heure — ${boutique}`, periode, taille: 'grand',
        data: { horaire, base, boutique },
      })} />}>
      {graphe}
    </Bloc>
  );
}

/* --- Historique CA / conversion, pour une boutique ----------------------- */
function GrapheHistorique({ historique, base, boutique, periode, requete, fige }) {
  const { COL, CAT } = useTheme();
  const { estMobile } = useEcran();
  const axisStyle = { fill: COL.ink400, fontSize: 11 };
  const hauteur = fige ? 250 : (estMobile ? 240 : 210);
  // La conversion depend de la base de comptage : on la recalcule ici plutot
  // que de la figer dans l'instantane, pour garder le calcul en un seul endroit.
  const donnees = (historique || []).map((h) => {
    const cl = base === 'tous' ? h.vrais_clients : h.portefeuilles_clients;
    return { ...h, conv: cl ? Math.round((h.tickets / cl) * 1000) / 10 : null };
  });

  const graphe = (
    // Largeur minimale calculee sur le nombre de jours : un mois entier ne
    // tiendra jamais sur un telephone, il defilera.
    <CadreGraphe hauteur={hauteur} largeurMin={estMobile ? Math.max(460, donnees.length * 46) : 0}>
      {(l, h) => (
        <ComposedChart width={l} height={h} data={donnees}>
          <CartesianGrid stroke={COL.border} vertical={false} />
          <XAxis dataKey="jour" tick={{ ...axisStyle, fontSize: 10 }} tickFormatter={(j) => (j || '').slice(5)} axisLine={{ stroke: COL.border }} tickLine={false} />
          <YAxis yAxisId="l" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis yAxisId="r" orientation="right" tick={axisStyle} unit="%" axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11.5 }} />
          <Bar isAnimationActive={!fige} yAxisId="l" dataKey="ca" name="CA (DT)" fill={CAT[3]} radius={[3, 3, 0, 0]} />
          <Line isAnimationActive={!fige} yAxisId="r" type="monotone" dataKey="conv" name="Conversion (%)" stroke={COL.amber} strokeWidth={2} dot={{ r: 2 }} connectNulls />
        </ComposedChart>
      )}
    </CadreGraphe>
  );

  if (fige) return graphe;
  return (
    <Bloc titre="Historique" sous="Evolution du CA et de la conversion"
      action={<BoutonEpingler enFlux fabriquer={() => ({
        kind: 'historique', requete, titre: `Historique — ${boutique}`, periode, taille: 'grand',
        data: { historique, base, boutique },
      })} />}>
      {graphe}
    </Bloc>
  );
}

/* --- Detail complementaire (uniquement ce qui n'est pas deja en haut) --- */
function DetailBoutique({ b, horaire, historique, moyenneReseau, famillesReseau, base, periode, requete }) {
  const ecart = (v, r) => (v === null || v === undefined || !r ? null : Math.round(((v - r) / r) * 100));
  const vcB = chiffres(b, base);
  const vcR = chiffres(moyenneReseau, base);
  const comparaisons = [
    { label: 'Conversion', v: vcB.taux_conversion, r: vcR.taux_conversion, u: '%' },
    { label: 'Panier moyen', v: b.panier_moyen, r: moyenneReseau.panier_moyen, u: 'DT' },
    { label: `Prix moyen ${LIBELLE_PRIX_MOYEN}`, v: prixMoyenReference(b.familles), r: prixMoyenReference(famillesReseau), u: 'DT' },
    { label: 'Articles / ticket', v: b.upt, r: moyenneReseau.upt, u: '' },
  ];
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* En premier : ce que la boutique vend le plus, c'est la question
          posee des qu'on clique sur une boutique. */}
      <BlocFamilles titre={`Ce que ${b.boutique} vend le plus`}
        sous="Part de chaque famille produit sur la periode selectionnee"
        familles={b.familles} caTotal={b.ca} articlesTotal={b.articles}
        periode={periode} requete={{ ...requete, boutique: b.boutique }}
        titrePlan={`Familles — ${b.boutique}`} />
      <Bloc titre={`${b.boutique} — ecart avec la moyenne du reseau`}>
        <div style={{ display: 'grid', gap: 10 }}>
          {comparaisons.map((c) => {
            const e = ecart(c.v, c.r);
            const positif = e !== null && e >= 0;
            return (
              <div key={c.label} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: COL.ink700 }}>{c.label}</span>
                <span style={{ fontSize: 13, color: COL.ink900, fontVariantNumeric: 'tabular-nums' }}>
                  {fmt1(c.v)} {c.u} <span style={{ color: COL.ink400 }}>vs {fmt1(c.r)} {c.u}</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, minWidth: 56, textAlign: 'right', color: e === null ? COL.ink400 : positif ? COL.green : COL.red }}>
                  {e === null ? '—' : `${positif ? '+' : ''}${e}%`}
                </span>
              </div>
            );
          })}
        </div>
      </Bloc>
      <GrapheTraficHeure horaire={horaire} base={base} boutique={b.boutique} periode={periode}
        requete={{ ...requete, boutique: b.boutique }} />
      <GrapheHistorique historique={historique} base={base} boutique={b.boutique} periode={periode}
        requete={{ ...requete, boutique: b.boutique }} />
    </div>
  );
}

/* --- Les deux paquets de theme -------------------------------------------
   Definis ici parce qu'ils referencent des constantes declarees tout au long
   du fichier (teintes de familles, echelles de la heatmap). */
const THEME_CLAIR = {
  COL, CAT, SHADOW, SHADOW_HOVER, GROUPE_TINT, FAM_TINT, AUTRES_TINT,
  STOPS_PAR_METRIQUE,
  tintOf: (nom, i) => SHOP_TINT[nom] || CAT[i % 5],
};
const THEME_SOMBRE = {
  COL: COL_SOMBRE, CAT: CAT_SOMBRE, SHADOW: SHADOW_SOMBRE, SHADOW_HOVER: SHADOW_SOMBRE_HOVER,
  GROUPE_TINT: GROUPE_TINT_SOMBRE, FAM_TINT: FAM_TINT_SOMBRE, AUTRES_TINT: AUTRES_TINT_SOMBRE,
  STOPS_PAR_METRIQUE: { trafic: TRAFIC_STOPS_SOMBRE, ventes: VENTES_STOPS_SOMBRE, conversion: CONVERSION_STOPS_SOMBRE },
  tintOf: (nom, i) => SHOP_TINT_SOMBRE[nom] || CAT_SOMBRE[i % 5],
};

/* Un instantane fige garde les couleurs de statut telles qu'elles etaient au
   moment de l'epinglage — en clair. Sur fond nuit, ce vert et ce rouge sont
   trop sombres : on les retraduit vers leur equivalent de la palette sombre. */
const EQUIV_SOMBRE = {
  [COL.green]: COL_SOMBRE.green, [COL.amber]: COL_SOMBRE.amber,
  [COL.red]: COL_SOMBRE.red, [COL.accent]: COL_SOMBRE.accent, [COL.ink400]: COL_SOMBRE.ink400,
};
const versSombre = (c) => (c ? EQUIV_SOMBRE[c] || c : c);

/* --- Registre du plan de travail ----------------------------------------
   Associe un type de carte epinglee a son rendu. Les cartes ne stockent que
   des donnees serialisables : c'est ce registre qui sait les redessiner, a
   l'identique, avec l'instantane fige au moment de l'epinglage. `fige` coupe
   les interactions qui n'ont plus de sens hors du dashboard (selection de
   boutique, ré-epinglage). */
const RENDUS_PLAN = {
  repartition: (d) => (
    <CercleRepartition data={d.data} total={d.total} uniteTotal={d.uniteTotal} selection={null} onSelect={() => {}} fige />
  ),
  groupes: (d) => <CamembertGroupes groupes={d.groupes} ratio={d.ratio} base={d.base} fige />,
  familles: (d) => <CamembertFamilles familles={d.familles} caTotal={d.caTotal} articlesTotal={d.articlesTotal} fige />,
  heures: (d) => <CamembertHeuresVentes horaire={d.horaire} cible={d.cible} fige />,
  heatmap: (d) => <Heatmap hourlyByShop={d.hourlyByShop} boutiques={d.boutiques} selection={null} base={d.base} fige />,
  frequentation: (d) => <FrequentationCumulee hourlyByShop={d.hourlyByShop} boutiques={d.boutiques} base={d.base} fige />,
  trafic_heure: (d) => <GrapheTraficHeure horaire={d.horaire} base={d.base} boutique={d.boutique} fige />,
  historique: (d) => <GrapheHistorique historique={d.historique} base={d.base} boutique={d.boutique} fige />,
  kpi: (d, mode) => {
    // Les couleurs de statut sont figees en clair dans l'instantane : elles ne
    // sont retraduites que si le plan est en mode nuit.
    const teinte = mode === 'clair' ? (c) => c : versSombre;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ width: '100%' }}>
          <Kpi hero label={d.label} value={d.value} unite={d.unite} sub={d.sub} trend={d.trend}
            sparkline={d.sparkline} sparklineCouleur={teinte(d.sparklineCouleur)}
            couleurValeur={teinte(d.couleurValeur)} fige />
        </div>
      </div>
    );
  },
};

/* Le plan de travail impose sa palette a tout ce qu'il affiche : nuit pour
   travailler a l'ecran, clair pour projeter ou imprimer. */
/* --- Rejouer une page sur une autre periode --------------------------------
   Un element epingle est un instantane fige : il porte SA REPONSE. Depuis
   qu'il porte aussi SA QUESTION (`requete`), on peut la reposer sur d'autres
   dates et reconstruire la meme composition avec de nouveaux chiffres.

   C'est la fonction qui manquait pour que dupliquer une page ait un sens : la
   copie contenait jusqu'ici les donnees du mois d'origine.

   Reconstruit `data` a partir d'une reponse fraiche de /api/stats/business.
   Renvoie null si l'element ne peut pas etre rejoue — une boutique disparue
   de la periode, par exemple : mieux vaut le laisser tel quel, avec ses
   donnees d'origine, que d'inventer. */
function agregerHoraire(hourlyByShop, boutique) {
  const source = boutique
    ? { [boutique]: hourlyByShop?.[boutique] || [] }
    : (hourlyByShop || {});
  const parHeure = {};
  Object.values(source).forEach((arr) => {
    (arr || []).forEach((h) => {
      const slot = parHeure[h.heure] || (parHeure[h.heure] = { heure: h.heure, ca: 0, tickets: 0 });
      slot.ca += h.ca || 0;
      slot.tickets += h.tickets || 0;
    });
  });
  return Object.values(parHeure).sort((a, b) => a.heure - b.heure);
}

export function reconstruireCarte(kind, d, req) {
  if (!d || !req) return null;
  const base = req.base === 'tous' ? 'tous' : 'vrais';
  const g = d.global;
  const b = req.boutique ? (d.boutiques || []).find((x) => x.boutique === req.boutique) : null;
  // La boutique demandee n'existe pas sur cette periode : on ne rejoue pas.
  if (req.boutique && !b) return null;
  const vue = b || g;
  if (!vue) return null;

  switch (kind) {
    case 'repartition': {
      if (req.variante === 'ca') {
        return {
          data: d.boutiques.map((x) => ({ nom: x.boutique, valeur: x.ca })),
          total: g.ca, uniteTotal: 'DT',
        };
      }
      return {
        data: d.boutiques.map((x) => ({ nom: x.boutique, valeur: chiffres(x, base).clients })),
        total: chiffres(g, base).clients,
        uniteTotal: base === 'tous' ? 'visiteurs' : 'vrais clients',
      };
    }
    case 'groupes':
      return {
        groupes: vue.groupes,
        ratio: vue.portefeuilles ? vue.portefeuilles_clients / vue.portefeuilles : 1,
        base,
      };
    case 'familles':
      return {
        familles: b ? b.familles : d.familles_global,
        caTotal: vue.ca, articlesTotal: vue.articles,
      };
    case 'heures':
      return {
        horaire: agregerHoraire(d.hourly_by_shop, req.boutique),
        cible: req.boutique || 'toutes boutiques',
      };
    case 'heatmap':
    case 'frequentation':
      return { hourlyByShop: d.hourly_by_shop, boutiques: d.boutiques, base };
    case 'trafic_heure':
      return { horaire: d.hourly_by_shop?.[req.boutique] || [], base, boutique: req.boutique };
    case 'historique':
      return { historique: d.history_by_shop?.[req.boutique] || [], base, boutique: req.boutique };
    case 'kpi': {
      const vc = chiffres(vue, base);
      const famillesVue = b ? b.familles : d.familles_global;
      const agregat = agregatFamillesReference(famillesVue);
      const clientsVrais = chiffres(vue, 'vrais').clients;
      const pointe = agregerHoraire(d.hourly_by_shop, req.boutique)
        .map((h) => ({ heure: h.heure, v: h.tickets }))
        .sort((a, b2) => b2.v - a.v)[0];
      const uneVentePour = vue.tickets ? Math.round(clientsVrais / vue.tickets) : null;

      const mesures = {
        ca: () => ({
          label: "Chiffre d'affaires", value: fmt(vue.ca), unite: 'DT',
          sub: `${fmt(vue.tickets)} tickets · ${fmt(vue.articles)} articles`,
        }),
        clients_vrais: () => ({
          label: 'Vrais clients', value: fmt(clientsVrais),
          sub: uneVentePour ? `1 vente pour ${uneVentePour}` : "groupes d'achat, staff exclu",
        }),
        clients_tous: () => ({
          label: 'Tous les visiteurs', value: fmt(chiffres(vue, 'tous').clients),
          sub: `${fmt(vue.staff)} staff exclu · ${fmt(chiffres(vue, 'tous').ca_par_visiteur)} DT / visiteur`,
        }),
        conversion: () => ({
          label: 'Conversion', value: pct(vc.taux_conversion), sub: "tickets / groupes d'achat",
        }),
        panier: () => ({
          label: 'Panier moyen', value: fmt(vue.panier_moyen), unite: 'DT',
          sub: `${fmt1(vue.upt)} article(s) / ticket`,
        }),
        prix_chaussures: () => ({
          label: 'Prix moyen chaussures', value: fmt1(prixMoyenReference(famillesVue)), unite: 'DT',
          sub: agregat ? `${fmt(agregat.ca)} DT / ${fmt(agregat.qte)} art.` : `aucune vente ${LIBELLE_PRIX_MOYEN}`,
        }),
        heure_pointe: () => ({
          label: 'Heure de pointe', value: pointe ? `${pointe.heure}h` : '—',
          sub: pointe ? `${fmt(pointe.v)} tickets` : undefined,
        }),
      };
      const f = mesures[req.mesure];
      return f ? f() : null;
    }
    default:
      return null;
  }
}

/* --- Grandeur comparable d'un element epingle -----------------------------
   Deux graphes de meme nature poses cote a cote sur le plan de travail
   invitent a la comparaison : le plan affiche alors l'ecart entre eux. Encore
   faut-il savoir QUOI comparer — le total d'un anneau, le CA d'un historique,
   le trafic d'une journee. C'est le role de cette table.

   La valeur est derivee de l'instantane deja stocke : rien de nouveau n'est
   enregistre, et les elements epingles avant cette fonctionnalite se
   comparent aussi bien que les autres.

   `unite` sert de garde-fou : on ne rapproche que des grandeurs de meme
   nature, pour ne jamais afficher l'ecart entre un chiffre d'affaires et un
   nombre de visiteurs. */
const sommeDe = (liste, champ) => (liste || []).reduce((s, x) => s + (x?.[champ] || 0), 0);

/* Les KPI ne conservent que leur valeur mise en forme ("31 420", "191.3%",
   "38,46 DT"). On la relit : separateurs de milliers retires, virgule
   decimale ramenee au point. */
function nombreDepuisTexte(t) {
  if (typeof t === 'number') return t;
  if (typeof t !== 'string') return null;
  const nettoye = t.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const v = parseFloat(nettoye);
  return Number.isFinite(v) ? v : null;
}

/* Nombre de jours couverts par un instantane, lu sur son etiquette :
   "19/08/2026 → 20/08/2026 · Reseau · vrais clients" en couvre deux,
   "20/08/2026 · Reseau · vrais clients" un seul.

   Indispensable a la comparaison : un total de deux jours face a un total
   d'un seul ne mesure aucune evolution, seulement le fait qu'une periode est
   plus longue que l'autre. */
function joursCouverts(periode) {
  const bornes = String(periode || '').split('·')[0].split('→').map((x) => x.trim()).filter(Boolean);
  if (bornes.length < 2) return 1;
  const enDate = (s) => {
    const [j, m, a] = s.split('/').map(Number);
    return (j && m && a) ? new Date(a, m - 1, j) : null;
  };
  const [d1, d2] = bornes.map(enDate);
  if (!d1 || !d2) return 1;
  return Math.round((d2 - d1) / 86400000) + 1;
}

export function valeurComparable(kind, d, carte) {
  if (!d) return null;

  /* Le PERIMETRE accompagne la valeur : sans lui, on rapprochait le reseau
     entier d'une seule boutique. L'ecart obtenu (« -79,8 % ») ne mesurait
     alors aucune baisse — juste le fait qu'une boutique est une fraction du
     tout. On ne compare desormais qu'un reseau a un reseau, ou une boutique a
     une boutique, fut-elle differente : comparer deux boutiques entre elles
     est precisement l'usage recherche. */
  const req = carte ? deduireRequete(carte) : null;
  const boutique = req?.boutique
    // Les anneaux de repartition et la heatmap portent toujours sur le reseau.
    ?? (['repartition', 'heatmap', 'frequentation'].includes(kind) ? null : d.boutique ?? null);

  const fait = (valeur, unite, libelle, texte, uniteAffichee) => (
    Number.isFinite(valeur)
      ? {
        valeur, unite, libelle,
        // `unite` sert a decider ce qui est comparable et peut etre technique
        // ("kpi:Panier moyen") ; celle-ci s'affiche a cote de l'ecart.
        uniteAffichee: uniteAffichee ?? (unite.startsWith('kpi:') ? '' : unite),
        texte: texte ?? `${fmt1(valeur)} ${unite}`.trim(),
        boutique: boutique || null,
        // `reseau` ou `boutique` : c'est ce qui doit concorder.
        perimetre: boutique ? 'boutique' : 'reseau',
        periode: carte?.periode || '',
        // Doit concorder aussi : voir joursCouverts.
        jours: joursCouverts(carte?.periode),
      }
      : null
  );
  switch (kind) {
    case 'kpi': {
      const v = nombreDepuisTexte(d.value);
      // Le libelle du KPI fait office d'unite : on ne compare un panier moyen
      // qu'a un autre panier moyen.
      return fait(v, `kpi:${d.label}`, d.label,
        `${d.value}${d.unite ? ` ${d.unite}` : ''}`, d.unite || '');
    }
    case 'repartition':
      return fait(d.total, d.uniteTotal, `total ${d.uniteTotal}`);
    case 'groupes': {
      const total = Object.entries(d.groupes || {})
        .reduce((s, [k, v]) => s + Math.round((v || 0) * (d.ratio ?? 1)) * (d.base === 'tous' ? TAILLE_GROUPE[k] : 1), 0);
      return fait(total, d.base === 'tous' ? 'visiteurs' : "groupes d'achat", 'total');
    }
    case 'familles':
      return fait(d.caTotal, 'DT', 'CA');
    case 'heures':
      return fait(sommeDe(d.horaire, 'ca'), 'DT', 'CA');
    case 'historique':
      return fait(sommeDe(d.historique, 'ca'), 'DT', 'CA cumule');
    case 'trafic_heure':
      return fait(sommeDe(d.horaire, d.base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients'),
        d.base === 'tous' ? 'visiteurs' : "groupes d'achat", 'trafic');
    case 'frequentation':
    case 'heatmap': {
      const champ = d.base === 'tous' ? 'vrais_clients' : 'portefeuilles_clients';
      const total = Object.values(d.hourlyByShop || {}).reduce((s, arr) => s + sommeDe(arr, champ), 0);
      return fait(total, d.base === 'tous' ? 'visiteurs' : "groupes d'achat", 'trafic');
    }
    default:
      return null;
  }
}

/* --- Retrouver la question d'un element epingle avant qu'on l'enregistre ---
   Les elements epingles jusqu'ici ne portent que leur reponse. Demander de
   tout re-epingler serait inacceptable : leur etiquette de periode contient
   deja le perimetre et la base de comptage, et le reste se lit dans les
   donnees. On la reconstitue donc, plutot que d'exiger un nouveau travail.

   Format de l'etiquette : "13/08/2026 · MANAR CITY · vrais clients". */
const MESURE_PAR_LIBELLE = {
  "Chiffre d'affaires": 'ca',
  'Vrais clients': 'clients_vrais',
  'Tous les visiteurs': 'clients_tous',
  Conversion: 'conversion',
  'Panier moyen': 'panier',
  'Prix moyen chaussures': 'prix_chaussures',
  'Heure de pointe': 'heure_pointe',
};

export function deduireRequete(carte) {
  if (carte?.requete) return carte.requete;
  if (!carte?.periode) return null;

  const morceaux = String(carte.periode).split('·').map((x) => x.trim());
  if (morceaux.length < 3) return null;
  const perimetre = morceaux[1];
  const requete = {
    boutique: perimetre && perimetre !== 'Reseau' ? perimetre : null,
    base: morceaux[2].startsWith('tous') ? 'tous' : 'vrais',
  };

  if (carte.kind === 'kpi') {
    const mesure = MESURE_PAR_LIBELLE[carte.data?.label];
    if (!mesure) return null;   // libelle inconnu : on ne devine pas
    return { ...requete, mesure };
  }
  if (carte.kind === 'repartition') {
    return { ...requete, variante: carte.data?.uniteTotal === 'DT' ? 'ca' : 'clients' };
  }
  // Les elements par boutique doivent en avoir une : sans elle, rien a rejouer.
  if (['trafic_heure', 'historique'].includes(carte.kind) && !requete.boutique) {
    return carte.data?.boutique ? { ...requete, boutique: carte.data.boutique } : null;
  }
  return requete;
}

async function chargerBusiness({ from, to, apiBase = '' }) {
  const params = new URLSearchParams();
  if (from && to && from !== to) { params.set('date_from', from); params.set('date_to', to); }
  else params.set('date', to || from || '');
  params.set('days', 14);

  const jeton = localStorage.getItem('peak_token');
  const r = await fetch(`${apiBase}/api/stats/business?${params}`, {
    headers: jeton ? { Authorization: `Bearer ${jeton}` } : {},
  });
  if (!r.ok) throw new Error(`Le serveur a repondu ${r.status}`);
  return r.json();
}

const libellePeriodeDe = (d) => (d.nb_jours > 1
  ? `${jourCourt(d.date_from)} → ${jourCourt(d.date_to)}`
  : jourCourt(d.date));

/* Le titre suit le perimetre : changer de boutique sur un element doit changer
   son intitule, sinon deux cartes cote a cote porteraient le meme nom. */
function titrePour(kind, requete, data) {
  const b = requete.boutique;
  switch (kind) {
    case 'kpi': return data?.label || 'Indicateur';
    case 'trafic_heure': return `Trafic et ventes par heure — ${b}`;
    case 'historique': return `Historique — ${b}`;
    case 'familles': return b ? `Familles — ${b}` : 'Ce que le reseau vend le plus';
    case 'heures': return `Heures de vente — ${b || 'toutes boutiques'}`;
    case 'groupes': return b ? `Composition — ${b}` : 'Composition des visites';
    default: return null;   // anneaux de repartition, heatmap : toujours le reseau
  }
}

const libelleRequete = (d, requete) => [
  libellePeriodeDe(d),
  requete.boutique || 'Reseau',
  requete.base === 'tous' ? 'tous visiteurs' : 'vrais clients',
].join(' · ');

/* Un element peut etre reconstruit et pourtant ne rien contenir : la periode
   choisie peut n'avoir aucune vente ni aucun passage. Le dire vaut mieux que
   d'afficher un graphe vide, qu'on prendrait pour un defaut d'affichage. */
function estVide(kind, data) {
  if (!data) return true;
  const somme = (l, c) => (l || []).reduce((s, x) => s + (x?.[c] || 0), 0);
  switch (kind) {
    case 'kpi': return data.value === '—' || data.value === '0';
    case 'repartition': return !data.total;
    case 'groupes': return !Object.values(data.groupes || {}).some((v) => v > 0);
    case 'familles': return !(data.familles || []).length;
    case 'heures': return !somme(data.horaire, 'ca');
    case 'historique': return !(data.historique || []).length;
    case 'trafic_heure': return !(data.horaire || []).length;
    case 'heatmap':
    case 'frequentation': return !Object.keys(data.hourlyByShop || {}).length;
    default: return false;
  }
}

/* Rejoue une page entiere : une seule interrogation de l'API pour la nouvelle
   periode, puis chaque element est reconstruit a partir de SA question. La
   disposition, les tailles et les notes sont conservees telles quelles.

   Les elements qu'on ne sait pas rejouer gardent leurs donnees d'origine et
   sont signales : mieux vaut une page partiellement rejouee, ou l'on voit ce
   qui n'a pas suivi, qu'un element silencieusement faux. */
export async function rejouerPage(page, { from, to, apiBase = '' }) {
  const d = await chargerBusiness({ from, to, apiBase });

  let rejoues = 0;
  let conserves = 0;
  let vides = 0;
  const cartes = (page.cartes || []).map((c) => {
    const req = deduireRequete(c);
    const data = req ? reconstruireCarte(c.kind, d, req) : null;
    if (!data) {
      conserves += 1;
      return { ...c, indisponible: `Aucune donnee pour ${req?.boutique || 'ce perimetre'} sur ${libellePeriodeDe(d)}` };
    }
    rejoues += 1;
    const vide = estVide(c.kind, data);
    if (vide) vides += 1;
    return {
      ...c, data,
      requete: req,
      periode: libelleRequete(d, req),
      titre: titrePour(c.kind, req, data) || c.titre,
      indisponible: null,
      vide: vide ? `Aucune donnee sur ${libellePeriodeDe(d)}` : null,
    };
  });

  return { cartes, libelle: libellePeriodeDe(d), rejoues, conserves, vides };
}

/* Rejoue UN element : sa periode, sa boutique, ou les deux. C'est ce qui
   permet de poser cote a cote la meme mesure sur deux boutiques ou deux mois,
   et de laisser l'ecart se calculer. */
export async function rejouerUneCarte(carte, { from, to, boutique, apiBase = '' }) {
  const base = deduireRequete(carte);
  if (!base) throw new Error("La question qui a produit cet element n'a pas pu etre retrouvee : re-epinglez-le depuis le tableau de bord.");
  const d = await chargerBusiness({ from, to, apiBase });
  const requete = { ...base, ...(boutique !== undefined ? { boutique } : {}) };
  const data = reconstruireCarte(carte.kind, d, requete);
  if (!data) {
    throw new Error(`Aucune donnee pour ${requete.boutique || 'ce perimetre'} sur ${libellePeriodeDe(d)}`);
  }
  const vide = estVide(carte.kind, data);
  return {
    data, requete,
    periode: libelleRequete(d, requete),
    titre: titrePour(carte.kind, requete, data) || carte.titre,
    vide: vide ? `Aucune donnee sur ${libellePeriodeDe(d)}` : null,
  };
}

// Exporte : une page partagee, ouverte hors du tableau de bord, doit pouvoir
// redessiner les memes graphes a partir du meme instantane.
export const REGISTRE_PLAN = Object.fromEntries(
  Object.entries(RENDUS_PLAN).map(([kind, rendu]) => [
    kind,
    (d, mode) => (
      <ThemeGraphe.Provider value={mode === 'clair' ? THEME_CLAIR : THEME_SOMBRE}>
        {rendu(d, mode)}
      </ThemeGraphe.Provider>
    ),
  ])
);

/* ========================================================================= */
export default function BusinessTab({ dateFrom, dateTo, days = 14, apiBase = '', refreshKey = 0, onLoaded }) {
  const [data, setData] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [selection, setSelection] = useState(null);
  const [base, setBase] = useState('vrais');
  const [seuils, setSeuils] = useState(lireSeuils);
  const { estMobile } = useEcran();

  // Memorise le reglage : c'est un objectif commercial, pas un filtre d'une
  // seule consultation.
  useEffect(() => {
    try { localStorage.setItem(CLE_SEUILS, JSON.stringify(seuils)); } catch (e) { /* stockage indisponible */ }
  }, [seuils]);

  useEffect(() => {
    let annule = false;
    setCharge(true);
    setErreur(null);
    // Un seul jour -> ?date= (l'API remonte alors N jours d'historique pour
    // les sparklines). Une periode -> ?date_from=&date_to=.
    const params = new URLSearchParams();
    if (dateFrom && dateTo && dateFrom !== dateTo) {
      params.set('date_from', dateFrom);
      params.set('date_to', dateTo);
    } else {
      params.set('date', dateTo || dateFrom || '');
    }
    params.set('days', days);
    const token = localStorage.getItem('peak_token');
    fetch(`${apiBase}/api/stats/business?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error(`Le serveur a repondu ${r.status}`); return r.json(); })
      .then((j) => { if (!annule) { setData(j); setCharge(false); if (onLoaded) onLoaded(new Date()); } })
      .catch((e) => { if (!annule) { setErreur(e.message); setCharge(false); } });
    return () => { annule = true; };
    // onLoaded volontairement hors dependances : sa reference change a chaque
    // rendu du parent et relancerait la requete en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, days, apiBase, refreshKey]);

  // Les alertes dependent des seuils et de la base de comptage : changer l'un
  // ou l'autre doit reevaluer quelles boutiques sont en dessous.
  const alertes = useMemo(() => auditer(data, seuils, base), [data, seuils, base]);
  const boutiqueActive = useMemo(() => (data?.boutiques || []).find((b) => b.boutique === selection) || null, [data, selection]);

  const historiqueReseau = useMemo(() => {
    if (!data) return [];
    const parJour = {};
    Object.values(data.history_by_shop || {}).forEach((arr) => {
      arr.forEach((h) => {
        const slot = parJour[h.jour] || (parJour[h.jour] = { jour: h.jour, ca: 0 });
        slot.ca += h.ca || 0;
      });
    });
    return Object.values(parJour).sort((a, b) => a.jour.localeCompare(b.jour));
  }, [data]);

  const heureDePointe = useMemo(() => {
    if (!data) return null;
    const parHeure = {};
    const source = selection ? { [selection]: data.hourly_by_shop?.[selection] || [] } : (data.hourly_by_shop || {});
    Object.values(source).forEach((arr) => {
      (arr || []).forEach((h) => {
        const v = base === 'tous' ? h.vrais_clients : (h.portefeuilles_clients !== undefined ? h.portefeuilles_clients : h.portefeuilles);
        parHeure[h.heure] = (parHeure[h.heure] || 0) + (v || 0);
      });
    });
    const entrees = Object.entries(parHeure);
    if (!entrees.length) return null;
    entrees.sort((a, b) => b[1] - a[1]);
    return { heure: Number(entrees[0][0]), valeur: entrees[0][1] };
  }, [data, selection, base]);

  /* Ventes cumulees heure par heure, sur la periode chargee : la boutique
     selectionnee seule, ou toutes les boutiques additionnees. */
  const ventesParHeure = useMemo(() => {
    if (!data) return [];
    const source = selection ? { [selection]: data.hourly_by_shop?.[selection] || [] } : (data.hourly_by_shop || {});
    const parHeure = {};
    Object.values(source).forEach((arr) => {
      (arr || []).forEach((h) => {
        const slot = parHeure[h.heure] || (parHeure[h.heure] = { heure: h.heure, ca: 0, tickets: 0 });
        slot.ca += h.ca || 0;
        slot.tickets += h.tickets || 0;
      });
    });
    return Object.values(parHeure).sort((a, b) => a.heure - b.heure);
  }, [data, selection]);

  /* Le corps du tableau de bord. Ce n'est volontairement PAS un composant et
     il ne contient aucun hook : c'est une simple fonction appelee une fois les
     donnees disponibles, ce qui permet de garder un unique `return` enveloppe
     par le plan de travail (voir plus bas). */
  const rendreTableauDeBord = () => {
  const g = data.global;
  const vue = boutiqueActive || g;
  const vc = chiffres(vue, base);
  const vcReseau = chiffres(g, base);
  // Les deux volumes de frequentation sont affiches en permanence et a place
  // fixe : la bascule ne doit pas faire apparaitre, disparaitre ni deplacer
  // une carte. Elle continue de piloter le reste de la page (conversion,
  // cercles, heatmap), pas ces deux compteurs.
  const clientsVrais = chiffres(vue, 'vrais').clients;  // groupes d'achat, staff exclu
  const clientsTous  = chiffres(vue, 'tous').clients;   // personnes, staff exclu
  const caParVisiteur = chiffres(vue, 'tous').ca_par_visiteur;
  const uneVentePour = vue.tickets ? Math.round(clientsVrais / vue.tickets) : null;

  // Prix moyen : famille de reference de la vue courante (boutique ou reseau).
  const famillesVue = boutiqueActive ? boutiqueActive.familles : data.familles_global;
  const agregatChaussures = agregatFamillesReference(famillesVue);
  const prixChaussuresVue = prixMoyenReference(famillesVue);
  const histHero = joursComplets(selection ? (data.history_by_shop?.[selection] || []) : historiqueReseau);
  const serieHeroCA = histHero.map((h) => h.ca);
  const visiteursParBoutique = data.boutiques.map((b) => ({ nom: b.boutique, valeur: chiffres(b, base).clients }));
  const caParBoutique = data.boutiques.map((b) => ({ nom: b.boutique, valeur: b.ca }));

  /* Seuils de la vue affichee : ceux de la boutique ouverte si elle a ses
     propres exigences, ceux du reseau sinon. */
  const seuilsVue = seuilsDe(seuils, selection);

  /* La QUESTION posee, jointe a chaque instantane. Sans elle, un element
     epingle ne sait dire que sa reponse ; avec elle, on peut la reposer sur
     une autre periode et rejouer toute une page. */
  const requeteCourante = { boutique: selection, base };

  /* Etiquette figee sur chaque carte epinglee : elle doit suffire, seule, a
     savoir de quoi on parle une fois la carte posee sur le plan de travail
     (periode + perimetre + base de comptage). */
  const libellePeriode = [
    data.nb_jours > 1 ? `${jourCourt(data.date_from)} → ${jourCourt(data.date_to)}` : jourCourt(data.date),
    selection || 'Reseau',
    base === 'tous' ? 'tous visiteurs' : 'vrais clients',
  ].join(' · ');

  return (
    <div style={{
      display: 'grid', gap: estMobile ? 12 : 16, background: COL.bg, padding: 4, minWidth: 0,
      fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <Eyebrow>Business Intelligence</Eyebrow>
          <h2 style={{ margin: '4px 0 0', fontSize: estMobile ? 16 : 18, fontWeight: 700, color: COL.ink900 }}>
            {selection ? selection : 'Vue reseau'}
          </h2>
        </div>
        {/* Les trois controles ne tiennent pas sur une ligne de telephone :
            ils s'y repartissent sur toute la largeur au lieu de deborder. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: estMobile ? 8 : 10,
          flexWrap: 'wrap', width: estMobile ? '100%' : 'auto',
        }}>
          <ReglageSeuils seuils={seuils} onChange={setSeuils} boutique={selection} />
          <ToggleBase valeur={base} onChange={setBase} />
          <span style={{ fontSize: 11.5, color: COL.ink500, background: COL.surface, border: `1px solid ${COL.border}`, borderRadius: 20, padding: '5px 12px', boxShadow: SHADOW }}>
            {charge && '⟳ '}
            {data.nb_jours > 1
              ? `Du ${jourCourt(data.date_from)} au ${jourCourt(data.date_to)} · ${data.nb_jours} jours cumules`
              : `${jourCourt(data.date)} · ventes a jour a ${data.data_until !== null ? `${data.data_until}h` : '—'}`}
          </span>

          {/* La caisse ne remonte que trois fois par jour ; les cameras, en
              continu. Quand le trafic va plus loin que les ventes, le dire —
              sans quoi l'ecart passe pour un arret du comptage. */}
          {data.nb_jours === 1 && data.trafic_until != null && data.data_until != null
            && data.trafic_until > data.data_until && (
            <span title={`Les cameras ont compte jusqu'a ${data.trafic_until}h. Les ventes de ${data.data_until + 1}h a ${data.trafic_until}h arriveront au prochain import de caisse.`}
              style={{
                fontSize: 11.5, color: COL.amber, background: COL.amberBg,
                border: `1px solid ${COL.amberBorder}`, borderRadius: 20,
                padding: '5px 12px', boxShadow: SHADOW,
              }}>
              trafic en direct jusqu'a {data.trafic_until}h · ventes attendues
            </span>
          )}
        </div>
      </div>

      {/* Une actualisation qui echoue ne doit plus effacer le tableau : on
          garde les derniers chiffres obtenus et on le dit clairement. */}
      {erreur && (
        <div style={{
          display: 'flex', gap: 10, padding: '11px 14px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5,
          background: COL.redBg, border: `1px solid ${COL.redBorder}`,
        }}>
          <span style={{ fontWeight: 700, color: COL.red, flexShrink: 0 }}>Actualisation impossible</span>
          <span style={{ color: COL.ink700 }}>{erreur} — les chiffres affiches sont ceux du dernier chargement reussi.</span>
        </div>
      )}

      {alertes.map((a, i) => (
        <div key={i} style={{
          // En colonne sur telephone : un intitule et un texte cote a cote
          // reduisent le second a deux mots par ligne.
          display: 'flex', flexDirection: estMobile ? 'column' : 'row',
          gap: estMobile ? 3 : 10, padding: '11px 14px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5,
          background: a.niveau === 'error' ? COL.redBg : a.niveau === 'warn' ? COL.amberBg : COL.bgPale,
          border: `1px solid ${a.niveau === 'error' ? COL.redBorder : a.niveau === 'warn' ? COL.amberBorder : COL.borderStrong}`,
        }}>
          <span style={{ fontWeight: 700, color: a.niveau === 'error' ? COL.red : a.niveau === 'warn' ? COL.amber : CAT[0], flexShrink: 0 }}>
            {/* Les alertes de seuil portent leur propre intitule : « Donnees
                incoherentes » serait faux, la donnee est bonne. */}
            {a.titre || (a.niveau === 'error' ? 'Donnees incoherentes' : a.niveau === 'warn' ? 'Donnees partielles' : 'Information')}
          </span>
          <span style={{ color: COL.ink700 }}>{a.texte}</span>
        </div>
      ))}

      {/* Les grilles s'ajustent d'elles-memes (auto-fit), mais le seuil doit
          descendre sur telephone : a 150px de minimum, deux colonnes serrees
          tronquaient les montants. */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${estMobile ? 132 : 150}px,1fr))`, gap: estMobile ? 8 : 10 }}>
        <Kpi requete={{ ...requeteCourante, mesure: 'ca' }} hero label="Chiffre d'affaires" value={fmt(vue.ca)} unite="DT" sub={`${fmt(vue.tickets)} tickets · ${fmt(vue.articles)} articles`}
          trend={tendance(histHero)} sparkline={serieHeroCA} sparklineCouleur={couleurConv(vc.taux_conversion, seuilsVue)} periode={libellePeriode} />
        <Kpi requete={{ ...requeteCourante, mesure: 'clients_vrais' }} label="Vrais clients" value={fmt(clientsVrais)} periode={libellePeriode}
          sub={uneVentePour ? `1 vente pour ${uneVentePour}` : "groupes d'achat, staff exclu"} />
        <Kpi requete={{ ...requeteCourante, mesure: 'clients_tous' }} label="Tous les visiteurs" value={fmt(clientsTous)} periode={libellePeriode}
          sub={`${fmt(vue.staff)} staff exclu · ${fmt(caParVisiteur)} DT / visiteur`} />
        <Kpi requete={{ ...requeteCourante, mesure: 'conversion' }} label="Conversion" value={pct(vc.taux_conversion)} sub="tickets / groupes d'achat" periode={libellePeriode}
          couleurValeur={couleurConv(vc.taux_conversion, seuilsVue)} />
        <Kpi requete={{ ...requeteCourante, mesure: 'panier' }} label="Panier moyen" value={fmt(vue.panier_moyen)} unite="DT" sub={`${fmt1(vue.upt)} article(s) / ticket`} periode={libellePeriode} />
        {/* Valeur au centime : c'est un prix, l'arrondi a l'unite masquait
            l'ecart avec le chiffre de la caisse. Le sous-titre montre les
            deux termes de la division pour que le calcul soit verifiable. */}
        <Kpi requete={{ ...requeteCourante, mesure: 'prix_chaussures' }} label="Prix moyen chaussures" value={fmt1(prixChaussuresVue)} unite="DT" periode={libellePeriode}
          sub={agregatChaussures
            ? `${fmt(agregatChaussures.ca)} DT / ${fmt(agregatChaussures.qte)} art.`
            : `aucune vente ${LIBELLE_PRIX_MOYEN}`} />
        <Kpi requete={{ ...requeteCourante, mesure: 'heure_pointe' }} label="Heure de pointe" value={heureDePointe ? `${heureDePointe.heure}h` : '—'} periode={libellePeriode}
          sub={heureDePointe ? `${fmt(heureDePointe.valeur)} groupes` : undefined} />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COL.ink900 }}>Boutiques</h3>
          {selection ? (
            <button onClick={() => setSelection(null)} style={{ background: 'none', border: 'none', color: CAT[0], fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              ← Retour au reseau
            </button>
          ) : (
            <span style={{ fontSize: 12, color: COL.ink400 }}>Cliquez une boutique pour ouvrir son detail</span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${estMobile ? 200 : 230}px,1fr))`, gap: estMobile ? 8 : 10 }}>
          {data.boutiques.map((b) => (
            <CarteBoutique key={b.codemag} b={b} historique={data.history_by_shop?.[b.boutique]} base={base} seuils={seuils}
              actif={selection === b.boutique} onClick={() => setSelection(selection === b.boutique ? null : b.boutique)} />
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${estMobile ? 240 : 260}px,1fr))`, gap: estMobile ? 12 : 16 }}>
        <Bloc titre={base === 'tous' ? 'Repartition des visiteurs' : 'Repartition des vrais clients'}
          sous="Part de chaque boutique dans le trafic">
          <CercleRepartition data={visiteursParBoutique} total={vcReseau.clients} uniteTotal={base === 'tous' ? 'visiteurs' : 'vrais clients'}
            selection={selection} onSelect={setSelection} periode={libellePeriode}
            requete={{ ...requeteCourante, variante: 'clients' }}
            titrePlan={base === 'tous' ? 'Repartition des visiteurs' : 'Repartition des vrais clients'} />
        </Bloc>
        <Bloc titre="Repartition du chiffre d'affaires" sous="Part de chaque boutique dans les ventes">
          <CercleRepartition data={caParBoutique} total={g.ca} uniteTotal="DT" selection={selection} onSelect={setSelection}
            periode={libellePeriode} requete={{ ...requeteCourante, variante: 'ca' }}
            titrePlan="Repartition du chiffre d'affaires" />
        </Bloc>
        <CamembertHeuresVentes titre="Heures de vente" sous="Quand se font les ventes"
          horaire={ventesParHeure} cible={selection || 'toutes boutiques'} periode={libellePeriode}
          requete={requeteCourante} />
        <Bloc titre={selection ? `Composition — ${selection}` : 'Composition des visites'}
          sous="Taille des groupes qui entrent en boutique">
          <CamembertGroupes groupes={vue.groupes} ratio={vue.portefeuilles ? vue.portefeuilles_clients / vue.portefeuilles : 1} base={base}
            periode={libellePeriode} requete={requeteCourante}
            titrePlan={selection ? `Composition — ${selection}` : 'Composition des visites'} />
        </Bloc>
      </div>

      <FrequentationCumulee hourlyByShop={data.hourly_by_shop} boutiques={data.boutiques} base={base}
        periode={libellePeriode} requete={requeteCourante} />

      <Heatmap hourlyByShop={data.hourly_by_shop} boutiques={data.boutiques} selection={selection} base={base}
        periode={libellePeriode} requete={requeteCourante} />

      {boutiqueActive ? (
        <DetailBoutique b={boutiqueActive} horaire={data.hourly_by_shop?.[boutiqueActive.boutique]} historique={data.history_by_shop?.[boutiqueActive.boutique]} moyenneReseau={g} famillesReseau={data.familles_global} base={base} periode={libellePeriode} requete={requeteCourante} />
      ) : (
        <BlocFamilles titre="Ce que le reseau vend le plus"
          sous="Part de chaque famille produit, toutes boutiques confondues, sur la periode selectionnee"
          familles={data.familles_global} caTotal={g.ca} articlesTotal={g.articles}
          periode={libellePeriode} requete={requeteCourante} titrePlan="Ce que le reseau vend le plus" />
      )}
    </div>
  );
  };

  /* Un seul point de sortie, et le plan de travail TOUJOURS monte au meme
     endroit de l'arbre. C'est ce qui le fait survivre a un rechargement des
     donnees : place a l'interieur du rendu conditionnel, il etait demonte a
     chaque changement de date ou rafraichissement automatique, et l'espace de
     travail se refermait tout seul.
     On garde aussi l'ancien tableau a l'ecran pendant un rechargement : le
     remplacer par "Chargement…" faisait clignoter la page derriere le plan. */
  return (
    <PlanDeTravailProvider registre={REGISTRE_PLAN} comparateur={valeurComparable}
      rejoueur={(page, bornes) => rejouerPage(page, { ...bornes, apiBase })}
      rejoueurCarte={(carte, params) => rejouerUneCarte(carte, { ...params, apiBase })}
      deduireRequete={deduireRequete}
      boutiques={(data?.boutiques || []).map((b) => b.boutique)}
      apiBase={apiBase}>
      {data ? rendreTableauDeBord()
        : erreur ? (
          <div style={{ padding: 20, background: COL.redBg, border: `1px solid ${COL.redBorder}`, borderRadius: 10 }}>
            <strong style={{ color: COL.red, fontSize: 13 }}>Impossible de charger les donnees.</strong>
            <p style={{ color: COL.ink700, fontSize: 12.5, margin: '5px 0 0' }}>{erreur} — verifiez que l'API repond, puis rechargez.</p>
          </div>
        ) : (
          /* La hauteur est RESERVEE. Ce bloc ne faisait qu'une centaine de
             pixels ; quand les donnees arrivaient, le tableau de bord entier
             le remplacait et repoussait d'un coup tout ce qui se trouvait en
             dessous. Chrome mesurait un CLS de 0,52 — « mauvaise » au-dela de
             0,1 — sur ce seul decalage.

             Reserver un plein ecran ne rend pas le chargement plus rapide : il
             empeche la page de sauter, ce qui est une gene bien plus visible
             qu'une demi-seconde d'attente. */
          <div style={{
            minHeight: '100vh', paddingTop: 40, textAlign: 'center',
            color: COL.ink500, fontSize: 13,
          }}>Chargement des donnees…</div>
        )}
    </PlanDeTravailProvider>
  );
}
